import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import axios from 'axios';
import { ArrowLeft, Loader2, Calendar, User, Download, Send, RefreshCw, MessageSquare, Check, X, HelpCircle, ChevronDown, CheckCircle2, Clock, Paperclip, Eye, AlertTriangle, AlertCircle, Copy, PlayCircle, Mail, Save, Upload, Trash2, ExternalLink, FileText, IndianRupee } from 'lucide-react';
import { DEPARTMENTS } from '@cg-idea/common';
import DatePicker from 'react-datepicker';

export function IdeaDetails({ isTdx = false }: { isTdx?: boolean }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const rawUserRole = localStorage.getItem('userRole') || 'EMPLOYEE';
  const viewAsEmployee = searchParams.get('viewAs') === 'employee' || rawUserRole === 'MIS_COMMITTEE';

  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const userSubCoreRole = localStorage.getItem('userSubCoreRole') || '';
  const userId = localStorage.getItem('userId');

  const getDashboardPath = () => {
    if (rawUserRole === 'ADMIN' || rawUserRole === 'APPROVAL_TEAM') return '/admin';
    if (rawUserRole === 'COMMITTEE_HEAD') return '/committee-head';
    if (rawUserRole === 'COMMITTEE_MEMBER') return '/committee-member';
    if (rawUserRole === 'LEADER') return '/leader';
    if (rawUserRole === 'EXTERNAL_COMMITTEE') return '/external-committee';
    if (rawUserRole === 'EXECUTION_COMMITTEE') return '/execution-committee';
    return '/dashboard';
  };
  const [reviewStatus, setReviewStatus] = useState<'APPROVED' | 'REJECTED' | 'MORE_INFO_REQUESTED' | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewError, setReviewError] = useState('');

  // Evaluation states
  const [activeAction, setActiveAction] = useState<'EVALUATE' | 'REJECT' | 'DUPLICATE' | 'UNDUPLICATE' | 'HOLD' | 'UNREJECT' | 'UNHOLD' | 'UNHOLD_EXTERNAL' | 'CHANGE_DEPT'>('EVALUATE');
  const [actionReason, setActionReason] = useState('');
  const [evalData, setEvalData] = useState<{
    savingPotential: boolean;
    easeOfImplementation: boolean;
    timeOfImplementation: boolean;
    category: 'A' | 'B' | 'C' | '';
    comments: string;
  }>({
    savingPotential: false,
    easeOfImplementation: false,
    timeOfImplementation: false,
    category: '',
    comments: ''
  });
  const [evalAttachments, setEvalAttachments] = useState<any[]>([]);
  const [isUploadingEvalFiles, setIsUploadingEvalFiles] = useState(false);
  const [evalUploadError, setEvalUploadError] = useState('');
  const [evalError, setEvalError] = useState('');
  const [showEvalToast, setShowEvalToast] = useState(false);
  const [showValToast, setShowValToast] = useState(false);

  // Email Confirmation State
  const [emailApprovedAmount, setEmailApprovedAmount] = useState('');
  const [emailReason, setEmailReason] = useState('');
  const [emailConfirmationDate, setEmailConfirmationDate] = useState(() => new Date().toISOString().substring(0, 10));
  const [emailAttachments, setEmailAttachments] = useState<any[]>([]);
  const [isUploadingEmailFiles, setIsUploadingEmailFiles] = useState(false);
  const [emailUploadError, setEmailUploadError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showEmailToast, setShowEmailToast] = useState(false);

  const [headAction, setHeadAction] = useState<'NONE' | 'REJECT' | 'APPROVE'>('NONE');

  // Assignment State
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState('');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [headActionReason, setHeadActionReason] = useState('');
  const [headActionError, setHeadActionError] = useState('');
  const [extAction, setExtAction] = useState<'NONE' | 'REJECT' | 'APPROVE' | 'HOLD'>('NONE');
  const [extActionReason, setExtActionReason] = useState('');
  const [extActionError, setExtActionError] = useState('');
  const [isRevisingSavings, setIsRevisingSavings] = useState(false);
  const [revisedSavings, setRevisedSavings] = useState('');

  const [assignTeam, setAssignTeam] = useState({
    validationTeam: '',
    executionTeam: '',
    priority: 'MEDIUM' as 'HIGH' | 'MEDIUM' | 'LOW',
  });
  const [assignError, setAssignError] = useState('');

  // Validation & Accrual states
  const [currentCost, setCurrentCost] = useState('');
  const [proposeSolution, setProposeSolution] = useState('');
  const [savingType, setSavingType] = useState('');
  const [annualSavings, setAnnualSavings] = useState('');
  const [oneTimeInvestment, setOneTimeInvestment] = useState('');
  const [implementationTimeline, setImplementationTimeline] = useState('');
  const [risk, setRisk] = useState('');
  const [notes, setNotes] = useState('');
  const [showReviewConfirmModal, setShowReviewConfirmModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFullLogModal, setShowFullLogModal] = useState(false);
  const [valAttachments, setValAttachments] = useState<any[]>([]);
  const [isUploadingValFiles, setIsUploadingValFiles] = useState(false);
  const [valUploadError, setValUploadError] = useState('');
  const [valError, setValError] = useState('');
  const [currentCostError, setCurrentCostError] = useState('');
  const [annualSavingsError, setAnnualSavingsError] = useState('');

  // Owner search state
  const ownerDropdownRef = React.useRef<HTMLDivElement>(null);
  const [ownerSearchInput, setOwnerSearchInput] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [ownerSuggestions, setOwnerSuggestions] = useState<any[]>([]);
  const [isSearchingOwner, setIsSearchingOwner] = useState(false);

  let committeeStatus = 'none';

  const handleCurrencyChange = (setter: any) => (e: any) => {
    const val = e.target.value;
    if (!val) {
      setter('');
      return;
    }
    const digits = val.replace(/\D/g, '');
    if (!digits) {
      setter('');
      return;
    }
    setter('₹' + Number(digits).toLocaleString('en-IN'));
  };

  // Stealth mode states
  const [isTdxModalOpen, setIsTdxModalOpen] = useState(false);
  const [isChangeDeptOpen, setIsChangeDeptOpen] = useState(false);
  const [newDept, setNewDept] = useState('');
  const [deptSearchQuery, setDeptSearchQuery] = useState('');
  const [showDeptSearchDropdown, setShowDeptSearchDropdown] = useState(false);
  const changeDeptDropdownRef = useRef<HTMLDivElement>(null);
  const [changeDeptReason, setChangeDeptReason] = useState('');
  const [changeDeptError, setChangeDeptError] = useState('');

  const [isWipModalOpen, setIsWipModalOpen] = useState(false);
  const [wipAmount, setWipAmount] = useState('');
  const [wipError, setWipError] = useState('');

  const [tdxData, setTdxData] = useState({
    title: '',
    description: '',
    department: '',
    category: '',
    createdAt: ''
  });
  const tdxMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.put(`/admin/ideas/${id}/tdx007`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });
      setIsTdxModalOpen(false);
      alert('TDX Stealth Edit successful.');
    },
    onError: (err: any) => {
      alert(err.response?.data?.error?.message || err.message || 'TDX update failed');
    }
  });

  // 1. Fetch idea details (hydrated with download links)
  const { data: idea, isLoading, error } = useQuery({
    queryKey: ['ideaDetails', id],
    queryFn: async () => {
      const res = await apiClient.get(`/ideas/${id}`);
      const fetchedIdea = res.data.data;
      if (fetchedIdea) {
        setAssignTeam({
          validationTeam: fetchedIdea.validationTeam || '',
          executionTeam: fetchedIdea.executionTeam || '',
          priority: fetchedIdea.priority || 'MEDIUM',
        });
        setOwnerSearchInput(fetchedIdea.validation?.owner || '');
        setImplementationTimeline(fetchedIdea.validation?.implementationTimeline || '');
        setRisk(fetchedIdea.validation?.risk || '');
        setNotes(fetchedIdea.validation?.notes || '');
        setCurrentCost(fetchedIdea.validation?.currentCost || '');
        setProposeSolution(fetchedIdea.validation?.proposeSolution || '');
        setSavingType(fetchedIdea.validation?.savingType || '');
        setAnnualSavings(fetchedIdea.validation?.annualSavings || '');
        setOneTimeInvestment(fetchedIdea.validation?.oneTimeInvestment || '');
        // Remove 'Z' to properly load into datetime-local without timezone shifting issues if not desired,
        // but it's safer to format to local time string
        const dateObj = new Date(fetchedIdea.createdAt);
        // adjust for timezone offset for input
        const tzoffset = dateObj.getTimezoneOffset() * 60000;
        const localISOTime = (new Date(dateObj.getTime() - tzoffset)).toISOString().slice(0, 16);

        setTdxData({
          title: fetchedIdea.title || '',
          description: fetchedIdea.description || '',
          department: fetchedIdea.department || '',
          category: fetchedIdea.category || '',
          createdAt: localISOTime
        });
      }
      return fetchedIdea;
    },
    enabled: !!id,
  });

  const isOwner = idea?.employeeId === userId;
  const userRole = viewAsEmployee ? 'EMPLOYEE' : rawUserRole;
  const isModerator = userRole === 'COMMITTEE_MEMBER';

  // Fetch Evaluations
  const { data: evaluations } = useQuery({
    queryKey: ['ideaEvaluations', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/ideas/${id}/evaluations`);
      return res.data.data;
    },
    enabled: !!id && (isModerator || userRole === 'ADMIN' || userRole === 'COMMITTEE_HEAD' || userRole === 'EXTERNAL_COMMITTEE' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'LEADER'),
  });

  // Fetch department groups for change department dropdown
  const { data: departmentGroupsData } = useQuery({
    queryKey: ['department-groups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/department-groups');
      return res.data.data || [];
    },
    enabled: !!id && (isModerator || userRole === 'ADMIN' || userRole === 'COMMITTEE_HEAD' || userRole === 'EXTERNAL_COMMITTEE' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'LEADER'),
  });

  // Fetch distinct departments for change department dropdown
  const { data: distinctDepartmentsData } = useQuery({
    queryKey: ['departments-distinct'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/departments/distinct');
      return res.data.data || [];
    },
    enabled: !!id && (isModerator || userRole === 'ADMIN' || userRole === 'COMMITTEE_HEAD' || userRole === 'EXTERNAL_COMMITTEE' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'LEADER'),
  });

  React.useEffect(() => {
    if ((evaluations && evaluations.length > 0) || idea?.evaluationCategory) {
      const targetEval = evaluations?.[0];
      const effectiveCat = targetEval?.category || idea?.evaluationCategory || '';

      let sav = !!targetEval?.savingPotential;
      let ease = !!targetEval?.easeOfImplementation;
      let time = !!targetEval?.timeOfImplementation;

      if (!sav && !ease && !time && effectiveCat) {
        const catUpper = effectiveCat.toUpperCase();
        if (catUpper.includes('A')) {
          sav = true; ease = true; time = true;
        } else if (catUpper.includes('B')) {
          sav = true; ease = true;
        } else if (catUpper.includes('C')) {
          sav = true;
        }
      }

      setEvalData({
        savingPotential: sav,
        easeOfImplementation: ease,
        timeOfImplementation: time,
        category: effectiveCat,
        comments: targetEval?.comments || ''
      });
    }
  }, [evaluations, idea?.evaluationCategory, userId, userRole]);

  // Fetch Employees for Owner Dropdown
  const { data: allEmployees } = useQuery({
    queryKey: ['allEmployees'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/employees');
      return res.data.data;
    },
    enabled: isModerator || userRole === 'ADMIN' || userRole === 'COMMITTEE_HEAD' || userRole === 'LEADER',
  });

  // Fetch Eligible Assignees
  const { data: eligibleAssignees, isLoading: isLoadingEligibleAssignees } = useQuery({
    queryKey: ['eligibleAssignees', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/ideas/${id}/eligible-assignees`);
      return res.data.data;
    },
    enabled: !!id && userRole === 'COMMITTEE_HEAD' && isAssignmentModalOpen,
  });

  // Fetch Current Assignees
  const { data: currentAssignees, isLoading: isLoadingCurrentAssignees } = useQuery({
    queryKey: ['currentAssignees', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/ideas/${id}/committee-assignees`);
      return res.data.data;
    },
    enabled: !!id && userRole === 'COMMITTEE_HEAD',
  });

  React.useEffect(() => {
    if (isAssignmentModalOpen && currentAssignees) {
      setSelectedAssigneeIds(currentAssignees.map((a: any) => a.id));
    }
  }, [isAssignmentModalOpen, currentAssignees]);

  React.useEffect(() => {
    if (idea?.emailConfirmation) {
      setEmailApprovedAmount(idea.emailConfirmation.approvedAmount || '');
      setEmailReason(idea.emailConfirmation.reason || '');
      if (idea.emailConfirmation.confirmationDate) {
        const rawDate = idea.emailConfirmation.confirmationDate;
        const formatted = typeof rawDate === 'string' ? rawDate.substring(0, 10) : new Date(rawDate).toISOString().substring(0, 10);
        setEmailConfirmationDate(formatted);
      } else if (idea.emailConfirmation.createdAt) {
        const rawDate = idea.emailConfirmation.createdAt;
        const formatted = typeof rawDate === 'string' ? rawDate.substring(0, 10) : new Date(rawDate).toISOString().substring(0, 10);
        setEmailConfirmationDate(formatted);
      }
    }
    if (idea?.attachments) {
      const emailAtts = idea.attachments.filter((a: any) => a.source === 'EMAIL_HEAD_APPROVAL');
      if (emailAtts.length > 0) {
        setEmailAttachments(emailAtts);
      }
    }
  }, [idea]);

  const assignMembersMutation = useMutation({
    mutationFn: async (assigneeIds: string[]) => {
      const res = await apiClient.post(`/admin/ideas/${id}/committee-assignees`, { assigneeIds });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentAssignees', id] });
      setIsAssignmentModalOpen(false);
      alert('Assignments updated successfully.');
    },
    onError: (err: any) => {
      alert(err.response?.data?.error?.message || 'Failed to update assignments.');
    }
  });

  React.useEffect(() => {
    if (allEmployees && ownerSearchInput) {
      const search = ownerSearchInput.toLowerCase();
      const filtered = allEmployees.filter((emp: any) =>
        (emp.name && emp.name.toLowerCase().includes(search)) ||
        (emp.cgId && emp.cgId.toLowerCase().includes(search)) ||
        (emp.email && emp.email.toLowerCase().includes(search))
      ).slice(0, 5); // top 5 suggestions
      setOwnerSuggestions(filtered);
    } else {
      setOwnerSuggestions([]);
    }
  }, [ownerSearchInput, allEmployees]);

  // 2. Post new comment mutation
  const commentMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await apiClient.post(`/ideas/${id}/comments`, { content });
      return res.data.data;
    },
    onSuccess: () => {
      setCommentText('');
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });
    },
  });

  // 3. Admin review action mutation
  const reviewMutation = useMutation({
    mutationFn: async (payload: { status: string; comment: string; validation?: any }) => {
      const res = await apiClient.post(`/admin/ideas/${id}/review`, payload);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      setReviewStatus(null);
      setReviewComment('');
      setActiveAction('EVALUATE');
      setActionReason('');
      setHeadAction('NONE');
      setHeadActionReason('');
      setHeadActionError('');
      setExtAction('NONE');
      setExtActionReason('');
      setExtActionError('');
      setIsRevisingSavings(false);
      setRevisedSavings('');
      setValAttachments([]);
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });

      // If validation payload was present, show toaster
      if (variables.validation) {
        setShowValToast(true);
        setTimeout(() => setShowValToast(false), 3000);
      }
    },
    onError: (err: any) => {
      setReviewError(err.response?.data?.error?.message || 'Failed to submit review.');
    }
  });

  // 4. Admin assign action mutation
  const assignMutation = useMutation({
    mutationFn: async (payload: typeof assignTeam) => {
      const res = await apiClient.patch(`/admin/ideas/${id}/assign`, payload);
      return res.data.data;
    },
    onSuccess: () => {
      setAssignError('');
      alert('Idea assigned successfully!');
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });
    },
    onError: (err: any) => {
      setAssignError(err.response?.data?.error?.message || 'Failed to assign idea.');
    }
  });

  // Change department mutation
  const changeDeptMutation = useMutation({
    mutationFn: async (payload: { department: string; reason: string }) => {
      const res = await apiClient.patch(`/admin/ideas/${id}/department`, payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });
      setIsChangeDeptOpen(false);
      setNewDept('');
      setChangeDeptReason('');
      setChangeDeptError('');
      navigate(getDashboardPath());
    },
    onError: (err: any) => {
      setChangeDeptError(err.response?.data?.error?.message || err.message || 'Failed to update department.');
    }
  });

  // 5. Evaluation mutation
  const evalMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post(`/admin/ideas/${id}/evaluate`, payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });
      setEvalAttachments([]);
      setEvalError('');
      setShowEvalToast(true);
      setTimeout(() => setShowEvalToast(false), 3000);
      queryClient.invalidateQueries({ queryKey: ['ideaEvaluations', id] });
    },
    onError: (err: any) => {
      setEvalError(err.response?.data?.error?.message || 'Failed to submit evaluation.');
    }
  });

  const emailConfirmationMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post(`/admin/ideas/${id}/email-confirmation`, payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideaDetails', id] });
      setEmailError('');
      setShowEmailToast(true);
      setTimeout(() => setShowEmailToast(false), 3000);
    },
    onError: (err: any) => {
      setEmailError(err.response?.data?.error?.message || 'Failed to save email confirmation.');
    }
  });

  const hasEvaluations = evaluations && evaluations.length > 0;
  const isAllowedMember = (userRole === 'ADMIN' || userRole === 'COMMITTEE_MEMBER' || userRole === 'LEADER' || userRole === 'COMMITTEE_HEAD') && userSubCoreRole !== 'EXECUTOR';
  const isMisApproved = idea?.misReview?.misStatus === 'APPROVED';
  const isMisDuplicate = idea?.status === 'DUPLICATE' && idea?.timelines?.slice().reverse().find((entry: { statusTo?: string; action?: string }) => entry.statusTo === 'DUPLICATE')?.action === 'MIS_REJECTED';
  const isEvalEditable = !isMisApproved && isAllowedMember && !['REJECTED', 'DUPLICATE', 'APPROVED', 'PENDING_EXECUTION_APPROVAL', 'EXECUTION_APPROVED'].includes(idea?.status || '');

  // We will define isEvalFormEditable below after the hold variables are defined
  const isEvalCompleted = !!hasEvaluations;
  const isValidationEditable = !isMisApproved && hasEvaluations && (userRole === 'COMMITTEE_HEAD' || userRole === 'ADMIN' || (isModerator && userSubCoreRole !== 'EXECUTOR')) && !['REJECTED', 'DUPLICATE', 'APPROVED', 'PENDING_EXECUTION_APPROVAL', 'EXECUTION_APPROVED'].includes(idea?.status || '');
  const isValidationCompleted = ['APPROVED', 'PENDING_EXECUTION_APPROVAL', 'EXECUTION_APPROVED'].includes(idea?.status || '');

  const savingsRevisionLog = idea?.timelines?.slice().reverse().find((t: any) => t.comment?.includes('[Annual Savings revised'));

  const holdLog = idea?.timelines?.slice().reverse().find((t: any) => t.statusTo === 'MORE_INFO_REQUESTED' && t.statusFrom !== 'MORE_INFO_REQUESTED');
  const holdReason = holdLog?.comment || 'No reason provided';
  const isHeadHold = holdLog?.statusFrom === 'PENDING_HEAD_APPROVAL' && idea?.status === 'MORE_INFO_REQUESTED';
  const isExternalHold = holdLog?.statusFrom === 'PENDING_EXTERNAL_APPROVAL' && idea?.status === 'MORE_INFO_REQUESTED';
  const isEvaluationHold = idea?.status === 'MORE_INFO_REQUESTED' && !isExternalHold && !isHeadHold;
  const isModeratorHold = isEvaluationHold;
  const isEvalFormEditable = isEvalEditable && !isEvaluationHold;
  let originalSavings = null;
  if (savingsRevisionLog) {
    const match = savingsRevisionLog.comment.match(/\[Annual Savings revised from (.*?) to/);
    if (match) {
      originalSavings = match[1];
    } else {
      originalSavings = 'N/A (Not Captured)';
    }
  }

  const submissionAttachments = idea?.attachments?.filter((a: any) => !a.source || a.source === 'SUBMISSION') || [];
  const existingEvalAttachments = idea?.attachments?.filter((a: any) => a.source === 'EVALUATION') || [];
  const existingValidationAttachments = idea?.attachments?.filter((a: any) => a.source === 'VALIDATION') || [];

  const handlePostComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    commentMutation.mutate(commentText);
  };

  const handleReject = () => {
    setActiveAction('REJECT');
    setActionReason('');
    setReviewError('');
  };

  const handleDuplicate = () => {
    setActiveAction('DUPLICATE');
    setActionReason('');
    setReviewError('');
  };

  const handleHold = () => {
    setActiveAction('HOLD');
    setActionReason('');
    setReviewError('');
  };

  const handleActionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (actionReason.trim().length < 5) {
      setReviewError('A reason of at least 5 characters is required.');
      return;
    }
    setReviewError('');

    if (activeAction === 'REJECT') {
      reviewMutation.mutate({ status: 'REJECTED', comment: actionReason });
    } else if (activeAction === 'DUPLICATE') {
      reviewMutation.mutate({ status: 'DUPLICATE', comment: actionReason });
    } else if (activeAction === 'HOLD') {
      reviewMutation.mutate({ status: 'MORE_INFO_REQUESTED', comment: actionReason });
    } else if (activeAction === 'UNHOLD') {
      reviewMutation.mutate({ status: 'UNDER_REVIEW', comment: actionReason });
    } else if (activeAction === 'UNHOLD_EXTERNAL') {
      reviewMutation.mutate({ status: 'PENDING_EXTERNAL_APPROVAL', comment: actionReason });
    } else if (activeAction === 'UNREJECT') {
      const rejectedLog = idea.timelines?.slice().reverse().find((t: any) => t.statusTo === 'REJECTED');
      let previousStatus = rejectedLog?.statusFrom || 'PENDING_HEAD_APPROVAL';

      if (previousStatus === 'SUBMITTED') {
        previousStatus = 'UNDER_REVIEW';
      }

      reviewMutation.mutate({ status: previousStatus, comment: actionReason });
    } else if (activeAction === 'UNDUPLICATE') {
      const dupLog = idea.timelines?.slice().reverse().find((t: any) => t.statusTo === 'DUPLICATE');
      let previousStatus = dupLog?.statusFrom || 'UNDER_REVIEW';

      if (previousStatus === 'SUBMITTED') {
        previousStatus = 'UNDER_REVIEW';
      }

      reviewMutation.mutate({ status: previousStatus, comment: actionReason });
    }
  };

  const handleUnhold = () => {
    setActiveAction('UNHOLD');
    setActionReason('');
    setReviewError('');
  };

  const handleUnholdExternal = () => {
    setActiveAction('UNHOLD_EXTERNAL');
    setActionReason('');
    setReviewError('');
  };

  const handleUnreject = () => {
    setActiveAction('UNREJECT');
    setActionReason('');
    setReviewError('');
  };

  const handleUnduplicate = () => {
    setActiveAction('UNDUPLICATE');
    setActionReason('');
    setReviewError('');
  };

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewStatus) {
      setReviewError('Please select a review status');
      return;
    }
    if (reviewComment.trim().length < 5) {
      setReviewError('A review comment is required (minimum 5 characters)');
      return;
    }

    setReviewError('');
    reviewMutation.mutate({
      status: reviewStatus,
      comment: reviewComment,
    });
  };

  const handleAssignSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    assignMutation.mutate(assignTeam);
  };

  const handleCheckboxChange = (field: 'savingPotential' | 'easeOfImplementation' | 'timeOfImplementation', value: boolean) => {
    setEvalData((prev) => {
      const newState = { ...prev, [field]: value };
      const points = (newState.savingPotential ? 1 : 0) + (newState.easeOfImplementation ? 1 : 0) + (newState.timeOfImplementation ? 1 : 0);

      let newCategory: 'A' | 'B' | 'C' | '' = '';
      if (points === 3) newCategory = 'A';
      else if (points === 2) newCategory = 'B';
      else if (points === 1) newCategory = 'C';

      return { ...newState, category: newCategory };
    });
  };

  const handleEvalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!evalData.category) {
      setEvalError('Please check at least one criterion to calculate the category.');
      return;
    }
    const updatedFields = [];
    const formatVal = (v: any) => v === true ? 'Yes' : v === false ? 'No' : (v && v.toString().trim() !== '') ? v.toString() : 'None';

    const checkField = (name: string, oldV: any, newV: any) => {
      const o = formatVal(oldV);
      const n = formatVal(newV);
      if (o !== n) updatedFields.push(`${name}: '${o}' ➔ '${n}'`);
    };

    const targetEval = evaluations?.[0];

    checkField('Saving Potential', targetEval?.savingPotential, evalData.savingPotential);
    checkField('Ease of Implementation', targetEval?.easeOfImplementation, evalData.easeOfImplementation);
    checkField('Time of Implementation', targetEval?.timeOfImplementation, evalData.timeOfImplementation);
    checkField('Category', targetEval?.category, evalData.category);
    checkField('Comments', targetEval?.comments, evalData.comments);

    const attachmentsChanged = evalAttachments.length > 0;
    if (attachmentsChanged) updatedFields.push('Attachments updated');

    if (updatedFields.length === 0) {
      setEvalError('No changes detected to update.');
      return;
    }

    setEvalError('');

    const payload = {
      ...evalData,
      attachments: evalAttachments,
      updatedFieldsStr: updatedFields.length > 0 ? `Evaluation details updated:\n${updatedFields.map(f => `• ${f}`).join('\n')}` : undefined
    };
    evalMutation.mutate(payload);
  };

  const handleEmailFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingEmailFiles(true);
    setEmailUploadError('');

    try {
      const uploadedList: any[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 50 * 1024 * 1024) {
          throw new Error(`File ${file.name} exceeds 50MB limit`);
        }

        if (!file.type.startsWith('image/')) {
          throw new Error(`File ${file.name} is not an image`);
        }

        const presignedRes = await apiClient.get('/ideas/upload-url', {
          params: { fileName: file.name, contentType: file.type },
        });

        const { uploadUrl, fileKey } = presignedRes.data.data;
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type },
        });

        uploadedList.push({
          fileName: file.name,
          fileKey: fileKey,
          fileSize: file.size,
          fileType: file.type,
        });
      }

      setEmailAttachments(prev => [...prev, ...uploadedList]);
    } catch (err: any) {
      setEmailUploadError(err.message || 'Failed to upload files');
    } finally {
      setIsUploadingEmailFiles(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    const rawDigits = (emailApprovedAmount || '').replace(/\D/g, '');
    const numericAmount = Number(rawDigits);

    if (!rawDigits || isNaN(numericAmount) || numericAmount <= 0) {
      setEmailError('Annual Savings Amount (₹) is required and cannot be zero.');
      return;
    }

    const payload = {
      approvedAmount: emailApprovedAmount,
      reason: emailReason,
      confirmationDate: emailConfirmationDate,
      attachments: emailAttachments
    };
    emailConfirmationMutation.mutate(payload);
  };

  const canUnreject = () => {
    if (isMisApproved) return false;
    if (!idea || !idea.timelines) return false;
    if (userRole === 'EMPLOYEE' && !isModerator) return false;

    const rejectedLog = idea.timelines.slice().reverse().find((t: any) => t.statusTo === 'REJECTED');
    if (!rejectedLog) return false;

    const prevStatus = rejectedLog.statusFrom;

    // External committee rejection is final and irreversible by anyone EXCEPT the external committee.
    if (prevStatus === 'PENDING_EXTERNAL_APPROVAL') {
      return userRole === 'EXTERNAL_COMMITTEE' || rejectedLog.actorId === userId;
    }

    // Admins, Committee Heads, and Leaders can unreject ANY other idea.
    if (userRole === 'ADMIN' || userRole === 'COMMITTEE_HEAD' || userRole === 'LEADER') return true;

    // If the current user is the one who rejected it, they can unreject it.
    if (rejectedLog.actorId === userId) return true;

    if (!prevStatus) return false;
    // Committee members can unreject ideas rejected at their stage
    if ((prevStatus === 'SUBMITTED' || prevStatus === 'UNDER_REVIEW') && userRole === 'COMMITTEE_MEMBER') return true;
    // Execution committee can unreject ideas rejected at their stage
    if (prevStatus === 'PENDING_EXECUTION_APPROVAL' && userRole === 'EXECUTION_COMMITTEE') return true;
    return false;
  };

  const handleEvalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setIsUploadingEvalFiles(true);
    setEvalUploadError('');

    const files = Array.from(e.target.files);
    const uploadedList = [...evalAttachments];

    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        setEvalUploadError(`File ${file.name} exceeds the maximum size limit of 50 MB`);
        continue;
      }

      try {
        const presignedRes = await apiClient.get('/ideas/upload-url', {
          params: {
            fileName: file.name,
            contentType: file.type,
          },
        });
        const { uploadUrl, fileKey } = presignedRes.data.data;
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type },
        });
        uploadedList.push({
          fileName: file.name,
          fileKey: fileKey,
          fileSize: file.size,
          fileType: file.type,
          fileUrl: uploadUrl.split('?')[0],
        });
      } catch (err) {
        setEvalUploadError(`Failed to upload ${file.name}.`);
      }
    }
    setEvalAttachments(uploadedList);
    setIsUploadingEvalFiles(false);
  };

  const handleValFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setIsUploadingValFiles(true);
    setValUploadError('');

    const files = Array.from(e.target.files);
    const uploadedList = [...valAttachments];

    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        setValUploadError(`File ${file.name} exceeds the maximum size limit of 50 MB`);
        continue;
      }

      try {
        const presignedRes = await apiClient.get('/ideas/upload-url', {
          params: {
            fileName: file.name,
            contentType: file.type,
          },
        });
        const { uploadUrl, fileKey } = presignedRes.data.data;
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type },
        });
        uploadedList.push({
          fileName: file.name,
          fileKey: fileKey,
          fileSize: file.size,
          fileType: file.type,
        });
      } catch (err) {
        setValUploadError(`Failed to upload ${file.name}.`);
      }
    }
    setValAttachments(uploadedList);
    setIsUploadingValFiles(false);
  };

  if (isLoading) {
    return (
      <Layout title="Idea Preview">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-brand-gold animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !idea) {
    return (
      <Layout title="Idea Preview">
        <div className="max-w-md mx-auto text-center py-20 space-y-4">
          <p className="text-red-400 font-semibold text-sm">Failed to retrieve idea details.</p>
          <button
            onClick={() => navigate(getDashboardPath())}
            className="text-xs bg-slate-900 border border-slate-800 px-4 py-2 rounded-lg text-slate-300 hover:text-brand-gold hover:border-brand-gold/40 transition-all cursor-pointer"
          >
            Return to Dashboard
          </button>
        </div>
      </Layout>
    );
  }
  let timelineSource = idea?.timelines || [];
  if (userRole === 'EMPLOYEE' && !isModerator) {
    const submittedLog = timelineSource.find((t: any) => t.action === 'SUBMITTED');
    let latestItem = null;

    if (idea?.status === 'REJECTED') {
      const rejectedLog = timelineSource.slice().reverse().find((t: any) => t.statusTo === 'REJECTED');
      if (rejectedLog) latestItem = { ...rejectedLog, action: 'REJECTED', statusTo: 'REJECTED', statusFrom: '' };
    } else if (idea?.status === 'DUPLICATE') {
      const dupLog = timelineSource.slice().reverse().find((t: any) => t.statusTo === 'DUPLICATE');
      if (dupLog) latestItem = { ...dupLog, action: 'DUPLICATE', statusTo: 'DUPLICATE', statusFrom: '' };
    } else if (idea?.status === 'APPROVED' || idea?.status === 'PENDING_EXECUTION_APPROVAL' || idea?.status === 'EXECUTION_APPROVED') {
      const approvedLog = timelineSource.slice().reverse().find((t: any) => t.statusTo === 'APPROVED' || t.statusTo === 'PENDING_EXECUTION_APPROVAL' || t.action?.includes('APPROVED'));
      if (approvedLog) {
        latestItem = { ...approvedLog, action: 'APPROVED', statusTo: 'APPROVED', statusFrom: '' };
      } else {
        latestItem = {
          action: 'APPROVED',
          statusTo: 'APPROVED',
          statusFrom: '',
          comment: 'Idea has been approved.',
          createdAt: timelineSource.length > 0 ? timelineSource[timelineSource.length - 1].createdAt : new Date().toISOString()
        };
      }
    } else if (idea?.status && idea.status !== 'SUBMITTED') {
      latestItem = {
        action: 'UNDER_REVIEW',
        statusTo: 'UNDER_REVIEW',
        statusFrom: '',
        createdAt: timelineSource.length > 0 ? timelineSource[timelineSource.length - 1].createdAt : new Date().toISOString()
      };
    }

    timelineSource = submittedLog ? [submittedLog] : [];
    if (latestItem && latestItem.action !== 'UNDER_REVIEW') {
      timelineSource.push(latestItem);
    }
  }

  const renderedTimeline = timelineSource
    .filter((log: any) => {
      if (userRole === 'EMPLOYEE' && !isModerator) {
        return true; // Already strictly generated above
      }
      return true;
    })
    .map((log: any) => {
      const actionType = log.action ? log.action.replace(/^(COMMITTEE_MEMBER_|COMMITTEE_HEAD_|EXTERNAL_COMMITTEE_|ADMIN_)/, '') : '';
      let target = log.statusTo || log.action || '';
      if (actionType === 'APPROVED' || actionType === 'EVALUATED') {
        target = actionType;
      }

      let colorClass = 'bg-blue-500 ring-blue-100 text-blue-700';
      let Icon = <Send className="w-3 h-3 text-white" />;
      if (target === 'REJECTED') {
        colorClass = 'bg-red-500 ring-red-100 text-red-700';
        Icon = <X className="w-3.5 h-3.5 text-white" />;
      } else if (target === 'APPROVED' || target === 'EVALUATED') {
        colorClass = 'bg-emerald-500 ring-emerald-100 text-emerald-700';
        Icon = <Check className="w-3.5 h-3.5 text-white" />;
      } else if (log.action?.includes('UPDATED')) {
        colorClass = 'bg-indigo-500 ring-indigo-100 text-indigo-700';
        Icon = <RefreshCw className="w-3 h-3 text-white" />;
      } else if (target === 'MORE_INFO_REQUESTED') {
        colorClass = 'bg-amber-500 ring-amber-100 text-amber-700';
        Icon = <HelpCircle className="w-3.5 h-3.5 text-white" />;
      } else if (target === 'DUPLICATE') {
        colorClass = 'bg-slate-500 ring-slate-100 text-slate-700';
        Icon = <Copy className="w-3.5 h-3.5 text-white" />;
      } else if (target.startsWith('PENDING') || target === 'UNHOLD' || target.includes('UNREJECT')) {
        colorClass = 'bg-orange-500 ring-orange-100 text-orange-700';
        Icon = <Clock className="w-3 h-3 text-white" />;
      } else if (target === 'UNDER_REVIEW' || target === 'DEPARTMENT_CHANGED') {
        colorClass = 'bg-indigo-500 ring-indigo-100 text-indigo-700';
        Icon = <RefreshCw className="w-3 h-3 text-white" />;
      }

      let displayAction = log.action.replace(/^(COMMITTEE_MEMBER_|COMMITTEE_HEAD_|EXTERNAL_COMMITTEE_|ADMIN_)/, '').replace(/_/g, ' ');
      if (log.action.includes('MORE_INFO_REQUESTED')) {
        displayAction = 'HOLD';
      }

      if (displayAction === 'SUBMITTED' || displayAction === 'IDEA SUBMITTED') {
        displayAction = 'YET TO BE REVIEWED';
      } else if (displayAction === 'UNDER REVIEW') {
        displayAction = 'REVIEWED';
      } else if (displayAction === 'PENDING EXTERNAL APPROVAL') {
        displayAction = 'REQUESTED EC HEAD APPROVAL';
      }

      // Identify Unreject and Unhold transitions
      const resumeStatuses = ['UNDER_REVIEW', 'PENDING_HEAD_APPROVAL', 'PENDING_EXTERNAL_APPROVAL'];
      if (log.statusFrom === 'REJECTED' && resumeStatuses.includes(log.statusTo || '')) {
        displayAction = 'UNREJECTED';
        colorClass = 'bg-rose-500 ring-rose-100 text-rose-700';
        Icon = <RefreshCw className="w-3 h-3 text-white" />;
      } else if (log.statusFrom === 'MORE_INFO_REQUESTED' && resumeStatuses.includes(log.statusTo || '')) {
        displayAction = 'UNHOLD';
        colorClass = 'bg-orange-500 ring-orange-100 text-orange-700';
        Icon = <RefreshCw className="w-3 h-3 text-white" />;
      } else if (log.statusFrom === 'WORK_IN_PROGRESS' && log.statusTo === 'SUBMITTED') {
        displayAction = 'UN-WIP';
        colorClass = 'bg-slate-500 ring-slate-100 text-slate-700';
        Icon = <RefreshCw className="w-3 h-3 text-white" />;
      } else if (log.statusTo === 'WORK_IN_PROGRESS') {
        displayAction = 'WIP';
      } else if (log.action === 'EVALUATED' || log.action === 'EVALUATION_UPDATED' || log.action.endsWith('VALIDATED') || log.action.endsWith('VALIDATION_UPDATED')) {
        const actorTeam = log.actor?.subCoreRole ? log.actor.subCoreRole.replace(/_/g, ' ') + ' TEAM' : 'COST TEAM';
        if (log.action.includes('EVALUATED') || log.action.includes('EVALUATION')) {
          displayAction = log.action === 'EVALUATED' ? `EVALUATED BY ${actorTeam}` : `EVALUATION UPDATED BY ${actorTeam}`;
        } else {
          displayAction = log.action.endsWith('VALIDATION_UPDATED') ? `VALIDATION UPDATED BY COST CENTRE MEMBER TEAM` : `VALIDATED BY COST CENTRE MEMBER TEAM`;
        }
        colorClass = 'bg-emerald-500 ring-emerald-100 text-emerald-700';
        Icon = <Check className="w-3.5 h-3.5 text-white" />;
      } else if (log.statusTo === 'APPROVED' && log.statusFrom === 'PENDING_EXTERNAL_APPROVAL') {
        displayAction = 'APPROVED BY EXTERNAL COMMITTEE';
        colorClass = 'bg-emerald-500 ring-emerald-100 text-emerald-700';
        Icon = <Check className="w-3.5 h-3.5 text-white" />;
      } else if (log.action === 'DEPARTMENT_CHANGED') {
        displayAction = 'DEPARTMENT CHANGED';
      }

      const isUpdate = log.action?.includes('UPDATED') || log.action?.includes('EDITED');

      if (isUpdate) {
        return (
          <div key={log.id} className="relative text-xs py-1 mt-2 mb-4 group">
            {/* Beautiful hollow dot */}
            <div className="absolute -left-[28px] top-[17px] w-2.5 h-2.5 rounded-full border-[2px] border-indigo-400 bg-white ring-4 ring-white transition-colors group-hover:border-indigo-600 z-10" />

            {/* Connecting horizontal line */}
            <div className="absolute top-[21px] -left-[23px] w-[28px] h-[1.5px] bg-slate-200 group-hover:bg-indigo-200 transition-colors z-0"></div>

            {/* Card */}
            <div className="bg-white border border-slate-100 rounded-xl p-3.5 ml-2 relative shadow-sm hover:shadow-md transition-all duration-300 group-hover:border-indigo-100">
              {/* Accent left border indicator */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-400 rounded-l-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="flex items-start justify-between gap-4">
                <p className="text-indigo-600 text-[11px] font-bold tracking-wider uppercase flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5 text-indigo-400 group-hover:rotate-180 transition-transform duration-700 ease-in-out" />
                  {displayAction}
                </p>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">{new Date(log.createdAt).toLocaleString('en-GB')}</p>
              </div>

              {log.actor?.name && (
                <div className="flex items-center gap-2 mt-2.5">
                  <div className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-600">
                    {log.actor.name.charAt(0)}
                  </div>
                  <p className="text-[11px] font-medium text-slate-500">
                    <span className="text-slate-700 font-semibold">{log.actor.name}</span>
                    <span className="text-slate-400 ml-1">({log.actor.role ? log.actor.role.replace(/_/g, ' ') : ''})</span>
                  </p>
                </div>
              )}

              {log.comment && (
                <div className="mt-3 bg-slate-50 border border-slate-100 rounded-lg p-2.5 relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  <p className="text-slate-600 font-medium italic text-[11px] leading-relaxed pl-1.5 whitespace-pre-line">
                    {log.comment}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={log.id} className="relative text-xs mb-6">
          <div className={`absolute -left-[35px] top-0 w-6 h-6 rounded-full flex items-center justify-center ring-4 ${colorClass.split(' text-')[0]}`}>
            {Icon}
          </div>
          {target === 'APPROVED' ? (
            <div className="inline-flex items-center bg-emerald-500 text-white px-2.5 py-0.5 rounded shadow-sm text-[11px] font-bold tracking-wider uppercase mb-1">
              {displayAction}
            </div>
          ) : (
            <p className={` ${colorClass.split(' ')[2]} text-xs flex items-center font-bold tracking-wide`}>
              {displayAction}
            </p>
          )}
          {log.actor?.name && !(userRole === 'EMPLOYEE' && !isModerator && target === 'REJECTED') && (
            <p className="font-medium text-slate-500 mt-0.5">
              by {log.actor.role ? log.actor.role.replace(/_/g, ' ') + ' ' : ''}{log.actor.name}
            </p>
          )}
          <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">{new Date(log.createdAt).toLocaleString('en-GB')}</p>
          {log.comment && !(userRole === 'EMPLOYEE' && !isModerator && target === 'APPROVED') && <p className="text-slate-600 mt-2 font-medium italic leading-relaxed bg-slate-50/80 p-3 rounded-lg border border-slate-200 whitespace-pre-line">{log.comment}</p>}
        </div>
      );
    });

  let displayStatus = idea.status.replace(/_/g, ' ');
  let displayColor = 'bg-slate-100 text-slate-700 border border-slate-200';

  if (idea.status === 'SUBMITTED') {
    displayStatus = 'YET TO BE REVIEWED';
  } else if (idea.status === 'UNDER_REVIEW') {
    displayStatus = 'REVIEWED';
    displayColor = 'bg-blue-50 text-blue-700 border border-blue-200';
  } else if (idea.status === 'PENDING_EXTERNAL_APPROVAL') {
    displayStatus = 'REQUESTED EC HEAD APPROVAL';
  } else if (idea.status === 'APPROVED') {
    displayColor = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  } else if (idea.status === 'REJECTED') {
    displayColor = 'bg-red-50 text-red-700 border border-red-200';
  } else if (idea.status === 'MORE_INFO_REQUESTED') {
    displayStatus = 'HOLD';
    displayColor = 'bg-amber-50 text-amber-700 border border-amber-200';
  } else if (idea.status === 'UNDER_REVIEW') {
    displayColor = 'bg-blue-50 text-blue-700 border border-blue-200';
  }

  // Employee Masking
  if (userRole === 'EMPLOYEE' && !isModerator) {
    if (idea.status === 'APPROVED' || idea.status === 'EXECUTION_APPROVED') {
      displayStatus = 'APPROVED';
      displayColor = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    } else if (['REJECTED', 'HEAD_REJECTED', 'EXTERNAL_REJECTED'].includes(idea.status)) {
      displayStatus = 'REJECTED';
      displayColor = 'bg-red-50 text-red-700 border border-red-200';
    } else if (idea.status === 'DUPLICATE') {
      displayStatus = 'DUPLICATE';
      displayColor = 'bg-slate-50 text-slate-700 border border-slate-200';
    } else if (idea.status === 'WORK_IN_PROGRESS') {
      displayStatus = 'SHORTLISTED';
      displayColor = 'bg-cyan-50 text-cyan-700 border border-cyan-200';
    } else if (idea.status === 'SUBMITTED') {
      displayStatus = 'YET TO BE REVIEWED';
      const createdAt = new Date(idea.createdAt);
      const cutoffDate = new Date('2026-07-19T00:00:00.000+05:30'); // Midnight of 19th July IST
      if (createdAt < cutoffDate) {
        displayStatus = 'REVIEWED';
        displayColor = 'bg-blue-50 text-blue-700 border border-blue-200';
      }
    } else {
      displayStatus = 'REVIEWED';
      displayColor = 'bg-blue-50 text-blue-700 border border-blue-200';
    }
  }

  const finalTimeline = [...(renderedTimeline || [])];

  if (userRole === 'EMPLOYEE' && !isModerator) {
    const isPastSubmitted = idea.status !== 'SUBMITTED' || (new Date(idea.createdAt) < new Date('2026-07-19T00:00:00.000+05:30'));

    if (isPastSubmitted) {
      // Find the index to insert synthetic cards. They should go AFTER the submitted log, but BEFORE the final log (like APPROVED or REJECTED)
      // `renderedTimeline` contains SUBMITTED (index 0) and potentially APPROVED/REJECTED/DUPLICATE (index 1).
      // We will insert between index 0 and 1 by splicing the array.

      const syntheticCards = [];

      syntheticCards.push(
        <div key="under-review-synthetic" className="relative text-xs mb-6 animate-in fade-in zoom-in duration-300">
          <div className="absolute -left-[35px] top-0 w-6 h-6 rounded-full flex items-center justify-center ring-4 bg-indigo-500 ring-indigo-100 text-indigo-700">
            <RefreshCw className="w-3 h-3 text-white" />
          </div>
          <p className="text-indigo-700 text-xs flex items-center font-bold tracking-wide uppercase">
            REVIEWED
          </p>
          <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
            Currently Processing
          </p>
          <p className="text-slate-600 mt-2 font-medium italic leading-relaxed bg-slate-50/80 p-3 rounded-lg border border-slate-200 whitespace-pre-line">
            Your idea has been received and is currently being evaluated by the committee.
          </p>
        </div>
      );

      const wasShortlisted = idea.timelines?.some((t: any) => t.statusTo === 'WORK_IN_PROGRESS') || displayStatus === 'SHORTLISTED';
      if (wasShortlisted) {
        syntheticCards.push(
          <div key="shortlisted-synthetic" className="relative text-xs mb-6 animate-in fade-in zoom-in duration-300">
            <div className="absolute -left-[35px] top-0 w-6 h-6 rounded-full flex items-center justify-center ring-4 bg-cyan-500 ring-cyan-100 text-cyan-700">
              <PlayCircle className="w-3 h-3 text-white" />
            </div>
            <p className="text-cyan-700 text-xs flex items-center font-bold tracking-wide uppercase">
              SHORTLISTED
            </p>
            <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
              Idea Shortlisted
            </p>
            <p className="text-slate-600 mt-2 font-medium italic leading-relaxed bg-slate-50/80 p-3 rounded-lg border border-slate-200 whitespace-pre-line">
              Your idea has been shortlisted by the committee and is currently in progress.
            </p>
          </div>
        );
      }

      // Insert synthetic cards after the first element (SUBMITTED)
      if (finalTimeline.length > 0) {
        finalTimeline.splice(1, 0, ...syntheticCards);
      } else {
        finalTimeline.push(...syntheticCards);
      }
    }
  }
  return (
    <Layout title="Idea Preview">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(getDashboardPath())}
            className="inline-flex items-center space-x-2 text-sm text-slate-500 hover:text-blue-600 transition-colors bg-transparent border-0 cursor-pointer font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>

          {['SUBMITTED', 'UNDER_REVIEW'].includes(idea.status) && !isMisApproved && (userRole === 'COMMITTEE_MEMBER' || userRole === 'COMMITTEE_HEAD' || userRole === 'LEADER' || userRole === 'ADMIN') && (
            <button
              type="button"
              onClick={() => {
                setIsWipModalOpen(true);
                setWipAmount('');
                setWipError('');
              }}
              disabled={reviewMutation.isPending}
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-1.5 px-4 rounded-lg text-xs flex items-center justify-center space-x-1.5 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50"
            >
              {reviewMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <PlayCircle className="w-3.5 h-3.5" />
              )}
              <span>WIP</span>
            </button>
          )}

          {idea.status === 'WORK_IN_PROGRESS' && (userRole === 'COMMITTEE_MEMBER' || userRole === 'COMMITTEE_HEAD' || userRole === 'LEADER' || userRole === 'ADMIN') && (
            (() => {
              const latestWipTimeline = [...(idea.timelines || [])].reverse().find((t: any) => t.comment?.includes('with estimated amount:'));
              const existingAmount = latestWipTimeline ? latestWipTimeline.comment.match(/with estimated amount:\s*(.+)/)?.[1] || '' : '';
              return (
                <>
                  {existingAmount && (
                    <div className="bg-cyan-50 border border-cyan-200 text-cyan-800 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center shadow-sm">
                      {existingAmount}
                    </div>
                  )}
                  {!isMisApproved && (
                  <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsWipModalOpen(true);
                      setWipAmount(existingAmount);
                      setWipError('');
                    }}
                    disabled={reviewMutation.isPending}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-1.5 px-4 rounded-lg text-xs flex items-center justify-center space-x-1.5 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50"
                  >
                    {reviewMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <PlayCircle className="w-3.5 h-3.5" />
                    )}
                    <span>Update WIP</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      reviewMutation.mutate({
                        status: 'UNDER_REVIEW',
                        comment: 'Reverted WIP',
                      });
                    }}
                    disabled={reviewMutation.isPending}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-1.5 px-4 rounded-lg text-xs flex items-center justify-center space-x-1.5 transition-all shadow-sm border border-slate-200 disabled:opacity-50"
                  >
                    {reviewMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                    <span>UN-WIP</span>
                  </button>
                  </>
                  )}
                </>
              );
            })()
          )}

          {userRole === 'COMMITTEE_HEAD' && (
            <div className="flex items-center space-x-3 bg-slate-50 border border-slate-200 pr-1 pl-3 py-1 rounded-full">
              <button
                onClick={() => { if (!isMisApproved) setIsAssignmentModalOpen(true); }}
                disabled={isMisApproved}
                className={`text-sm font-semibold flex items-center space-x-2 ${isMisApproved ? 'text-slate-500 cursor-default' : 'text-blue-700 hover:text-blue-800 transition-colors'}`}
              >
                <User className="w-4 h-4" />
                <span>Committee Assignments</span>
              </button>

              {isLoadingCurrentAssignees ? (
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse border-2 border-white"></div>
                </div>
              ) : currentAssignees && currentAssignees.length > 0 ? (
                <div className="flex -space-x-2">
                  {currentAssignees.slice(0, 3).map((assignee: any) => (
                    <div
                      key={assignee.id}
                      title={`${assignee.name} (${assignee.email})`}
                      className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center border-2 border-white shadow-sm cursor-help relative group"
                    >
                      {assignee.name.substring(0, 2).toUpperCase()}

                      <div className="absolute hidden group-hover:block bottom-full mb-2 bg-slate-800 text-white text-xs py-1 px-2 rounded whitespace-nowrap z-50">
                        {assignee.name}
                      </div>
                    </div>
                  ))}
                  {currentAssignees.length > 3 && (
                    <div
                      title={`+${currentAssignees.length - 3} more assignees`}
                      className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 font-bold text-xs flex items-center justify-center border-2 border-white shadow-sm cursor-help z-10"
                    >
                      +{currentAssignees.length - 3}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-slate-200 border-dashed flex items-center justify-center text-slate-400" title="No assignees yet">
                    <User className="w-4 h-4 opacity-50" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isTdx && userRole === 'ADMIN' && !isMisApproved && (
          <button
            onClick={() => setIsTdxModalOpen(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-md transition-all cursor-pointer"
          >
            TDX Edit
          </button>
        )}
      </div>

      {isMisApproved && (
        <p className="mb-4 text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2">
          This idea has been approved by the MIS team and can only be viewed.
        </p>
      )}

      {isWipModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-cyan-600" />
                Work In Progress
              </h3>
              <button
                onClick={() => setIsWipModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Estimated Amount (Mandatory) <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="₹0"
                value={wipAmount}
                onChange={handleCurrencyChange(setWipAmount)}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-slate-800 transition-all font-medium"
              />
              {wipError && <p className="text-red-500 text-xs mt-2 font-medium">{wipError}</p>}
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsWipModalOpen(false)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!wipAmount) {
                    setWipError('Amount is required.');
                    return;
                  }
                  setWipError('');
                  reviewMutation.mutate({
                    status: 'WORK_IN_PROGRESS',
                    comment: `Took ownership of the idea with estimated amount: ${wipAmount}`,
                  });
                  setIsWipModalOpen(false);
                }}
                disabled={!wipAmount || reviewMutation.isPending}
                className="px-4 py-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-700 rounded-xl transition-all shadow-sm shadow-cyan-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {reviewMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                Confirm WIP
              </button>
            </div>
          </div>
        </div>
      )}

      {isTdxModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center justify-between">
              <span>TDX Stealth Edit Mode</span>
              <button onClick={() => setIsTdxModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1 text-slate-700">Title</label>
                <input type="text" className="w-full border border-slate-200 p-2 rounded-lg" value={tdxData.title} onChange={e => setTdxData({ ...tdxData, title: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1 text-slate-700">Description</label>
                <textarea className="w-full border border-slate-200 p-2 rounded-lg h-32" value={tdxData.description} onChange={e => setTdxData({ ...tdxData, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-slate-700">Department</label>
                  <input type="text" className="w-full border border-slate-200 p-2 rounded-lg" value={tdxData.department} onChange={e => setTdxData({ ...tdxData, department: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 text-slate-700">Category</label>
                  <input type="text" className="w-full border border-slate-200 p-2 rounded-lg" value={tdxData.category} onChange={e => setTdxData({ ...tdxData, category: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1 text-red-600">Creation Date & Time</label>
                <input type="datetime-local" className="w-full border p-2 rounded-lg border-red-300 bg-red-50 text-red-800" value={tdxData.createdAt} onChange={e => setTdxData({ ...tdxData, createdAt: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setIsTdxModalOpen(false)} className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg font-medium transition-colors cursor-pointer">Cancel</button>
              <button
                onClick={() => tdxMutation.mutate({ ...tdxData, createdAt: new Date(tdxData.createdAt).toISOString() })}
                disabled={tdxMutation.isPending}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-colors cursor-pointer shadow-sm"
              >
                {tdxMutation.isPending ? 'Saving...' : 'Stealth Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAssignmentModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden p-6 max-h-[90vh] flex flex-col">
            <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center justify-between shrink-0">
              <span>Committee Assignments</span>
              <button onClick={() => setIsAssignmentModalOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer"><X className="w-5 h-5" /></button>
            </h3>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-900 flex items-center space-x-2 font-medium shrink-0 shadow-sm">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <span><strong>Note:</strong> Only 1 primary committee member can be assigned per idea. Selecting a user will assign them as the single evaluator.</span>
            </div>

            <div className="mb-4 shrink-0">
              <input
                type="text"
                placeholder="Search by name, email, or MP ID..."
                className="w-full border border-slate-200 p-2 rounded-lg text-sm"
                value={assignmentSearchTerm}
                onChange={e => setAssignmentSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex-1 overflow-y-auto mb-4 border border-slate-200 rounded-lg bg-slate-50">
              {isLoadingEligibleAssignees || isLoadingCurrentAssignees ? (
                <div className="flex justify-center items-center h-32 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  <span>Loading members...</span>
                </div>
              ) : eligibleAssignees && eligibleAssignees.length > 0 ? (
                <table className="w-full text-left text-sm text-slate-700">
                  <thead className="bg-slate-100 text-slate-600 font-semibold sticky top-0">
                    <tr>
                      <th className="p-3 border-b border-slate-200 w-12 text-center"></th>
                      <th className="p-3 border-b border-slate-200">Name</th>
                      <th className="p-3 border-b border-slate-200">ID</th>
                      <th className="p-3 border-b border-slate-200">Email</th>
                      <th className="p-3 border-b border-slate-200">Reporting To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleAssignees.filter((u: any) =>
                      !assignmentSearchTerm ||
                      u.name.toLowerCase().includes(assignmentSearchTerm.toLowerCase()) ||
                      u.email.toLowerCase().includes(assignmentSearchTerm.toLowerCase()) ||
                      (u.mpId && u.mpId.toLowerCase().includes(assignmentSearchTerm.toLowerCase()))
                    ).map((u: any) => (
                      <tr
                        key={u.id}
                        onClick={() => setSelectedAssigneeIds([u.id])}
                        className="border-b border-slate-200 hover:bg-blue-50/50 transition-colors cursor-pointer"
                      >
                        <td className="p-3 text-center">
                          <input
                            type="radio"
                            name="committeeAssigneeRadio"
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            checked={selectedAssigneeIds.includes(u.id)}
                            onChange={() => {
                              setSelectedAssigneeIds([u.id]);
                            }}
                          />
                        </td>
                        <td className="p-3 font-medium text-slate-900">{u.name}</td>
                        <td className="p-3">{u.mpId || '-'}</td>
                        <td className="p-3">{u.email}</td>
                        <td className="p-3">{u.reportsTo?.name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                  <User className="w-8 h-8 mb-2 opacity-50" />
                  <span>No eligible committee members found for this department.</span>
                </div>
              )}
            </div>

            <div className="shrink-0 flex justify-end space-x-3 mt-auto">
              <button onClick={() => setIsAssignmentModalOpen(false)} className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg font-medium transition-colors cursor-pointer">Cancel</button>
              <button
                onClick={() => assignMembersMutation.mutate(selectedAssigneeIds)}
                disabled={assignMembersMutation.isPending}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors cursor-pointer shadow-sm flex items-center"
              >
                {assignMembersMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Assignments
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Core content */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm space-y-6">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-bold tracking-wide border border-slate-200">
                  <span className="opacity-75 font-semibold mr-1">Department:</span>
                  <span className="uppercase">{idea.department}</span>
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900 break-all">{idea.title}</h1>
            </div>

            <hr className="border-slate-100" />

            <div>
              <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-3">Description / Solution</h3>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line break-words">{idea.description}</p>
            </div>

            {/* Impact stats */}
            {idea.costSaving != null && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Indicative Saving</span>
                  <p className="text-base font-bold text-emerald-600 mt-1">₹{Number(idea.costSaving).toLocaleString('en-IN')}</p>
                </div>
              </div>
            )}
          </div>

          {/* S3 Attachment Pre-signed Download Cards */}
          {submissionAttachments.length > 0 && (
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold font-display text-slate-800 mb-4">Uploaded Attachments</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {submissionAttachments.map((file: any) => (
                  <div
                    key={file.id}
                    className="bg-slate-50 border border-slate-200 p-4 rounded-xl flex items-center justify-between"
                  >
                    <div className="min-w-0 pr-2">
                      <p className="text-xs font-semibold text-slate-700 truncate">{file.fileName}</p>
                      <p className="text-[10px] text-slate-500 mt-1">{(file.fileSize / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={file.downloadUrl || file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View File"
                        className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 bg-white border border-slate-200 rounded-lg transition-colors cursor-pointer"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      <a
                        href={file.downloadUrl || file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        title="Download File"
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2 bg-white border border-slate-200 rounded-lg transition-colors cursor-pointer"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Moderator Evaluation Card */}
          {((isModerator && (userSubCoreRole !== 'EXECUTOR' || isEvalCompleted)) || userRole === 'COMMITTEE_HEAD' || userRole === 'ADMIN' || userRole === 'EXTERNAL_COMMITTEE' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'LEADER') && (
            <>
              <div className={`p-6 rounded-xl border shadow-md space-y-4 ${!isEvalEditable ? (isEvalCompleted ? 'bg-emerald-50/30 border-emerald-300' : 'bg-white border-slate-200') : (isEvalCompleted ? 'bg-emerald-50/30 border-emerald-300' : 'bg-white border-indigo-200')}`}>
                <div className="flex items-center justify-between">
                  <h3 className={`text-xs uppercase font-bold tracking-wider font-display flex items-center space-x-2 ${isEvalCompleted ? 'text-emerald-700' : 'text-indigo-700'}`}>
                    <span>Evaluate Idea {isEvalCompleted && '(Completed)'}</span>
                    {isEvalCompleted && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {isEvalEditable && (!evaluations || evaluations.length === 0) && (
                      <>
                        <button
                          type="button"
                          onClick={handleReject}
                          disabled={reviewMutation.isPending}
                          className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200 font-bold py-1.5 px-3 rounded-xl text-[10px] uppercase tracking-wider transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {reviewMutation.isPending ? 'Processing...' : 'Reject'}
                        </button>
                        {idea?.status !== 'MORE_INFO_REQUESTED' && (
                          <button
                            type="button"
                            onClick={handleHold}
                            disabled={reviewMutation.isPending}
                            className="bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-700 border border-amber-200 font-bold py-1.5 px-3 rounded-xl text-[10px] uppercase tracking-wider transition-colors disabled:opacity-50 cursor-pointer"
                          >
                            {reviewMutation.isPending ? 'Processing...' : 'Hold'}
                          </button>
                        )}
                      </>
                    )}
                    {idea?.status !== 'DUPLICATE' && !isMisApproved && userRole !== 'EXTERNAL_COMMITTEE' && userRole !== 'EXECUTION_COMMITTEE' && userSubCoreRole !== 'EXECUTOR' && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setIsChangeDeptOpen(!isChangeDeptOpen); setNewDept(idea?.department || ''); setChangeDeptError(''); }}
                          className="bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 border border-blue-200 font-bold py-1.5 px-3 rounded-xl text-[10px] uppercase tracking-wider transition-colors cursor-pointer"
                        >
                          Change Dept
                        </button>
                        <button
                          type="button"
                          onClick={handleDuplicate}
                          disabled={reviewMutation.isPending}
                          className="bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700 border border-orange-200 font-bold py-1.5 px-3 rounded-xl text-[10px] uppercase tracking-wider transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {reviewMutation.isPending ? 'Processing...' : 'Duplicate'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {evalError && <p className="text-red-500 text-xs font-semibold">{evalError}</p>}

                {isModeratorHold && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-4 animate-in fade-in slide-in-from-top-2 duration-300 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="text-amber-900 font-bold text-sm tracking-wide">Idea is on Hold (Committee Evaluation)</h4>
                        <p className="text-amber-700 text-xs mt-1 leading-relaxed">
                          <span className="font-semibold text-amber-800">Reason:</span> {holdReason}
                        </p>
                      </div>
                    </div>
                    {isAllowedMember && !isMisApproved && (
                      <button
                        type="button"
                        onClick={handleUnhold}
                        disabled={reviewMutation.isPending}
                        className="bg-amber-500 text-white hover:bg-amber-600 border border-amber-600 font-bold py-2 px-6 rounded-xl text-xs uppercase tracking-wider transition-colors disabled:opacity-50 cursor-pointer shadow-sm shrink-0 whitespace-nowrap self-end sm:self-auto"
                      >
                        {reviewMutation.isPending ? 'Processing...' : 'Unhold Idea'}
                      </button>
                    )}
                  </div>
                )}

                {idea?.status === 'DUPLICATE' && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 my-4 animate-in fade-in slide-in-from-top-2 duration-300 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="bg-slate-100 p-2 rounded-lg">
                        <Copy className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="text-slate-900 font-bold text-sm tracking-wide">Idea is Marked as Duplicate</h4>
                        <p className="text-slate-700 text-xs mt-1 leading-relaxed">
                          <span className="font-semibold text-slate-800">Reason:</span> {idea?.timelines?.slice().reverse().find((t: any) => t.statusTo === 'DUPLICATE')?.comment || 'No reason provided.'}
                        </p>
                        {isMisDuplicate && (
                          <p className="text-slate-600 text-xs mt-2">Only the MIS team can undo this duplicate.</p>
                        )}
                      </div>
                    </div>
                    {isAllowedMember && !isMisDuplicate && !isMisApproved && (
                      <button
                        type="button"
                        onClick={handleUnduplicate}
                        disabled={reviewMutation.isPending}
                        className="bg-red-600 text-white hover:bg-red-500 border border-red-500 font-bold py-2 px-6 rounded-xl text-xs uppercase tracking-wider transition-colors disabled:opacity-50 cursor-pointer shadow-sm shrink-0 whitespace-nowrap self-end sm:self-auto"
                      >
                        {reviewMutation.isPending ? 'Processing...' : 'Unmark as Duplicate'}
                      </button>
                    )}
                  </div>
                )}

                {isChangeDeptOpen && (
                  <div className="bg-blue-50/60 border border-blue-200 p-4 rounded-xl space-y-3 my-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-blue-800 flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Change Idea Department</span>
                      </h4>
                      <button type="button" onClick={() => setIsChangeDeptOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {changeDeptError && <p className="text-red-500 text-xs font-semibold">{changeDeptError}</p>}
                    <div className="grid grid-cols-2 gap-3">
                      <div ref={changeDeptDropdownRef} className="relative">
                        <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Department where idea to be implemented</label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search and select department..."
                            value={deptSearchQuery}
                            onChange={e => {
                              setDeptSearchQuery(e.target.value);
                              setNewDept('');
                              setShowDeptSearchDropdown(true);
                            }}
                            onFocus={() => setShowDeptSearchDropdown(true)}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                          />
                          <span className="absolute right-3 top-2.5 text-slate-400 pointer-events-none">
                            <ChevronDown className="w-4 h-4" />
                          </span>
                          {showDeptSearchDropdown && (
                            <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-52 overflow-y-auto divide-y divide-slate-100 animate-in slide-in-from-top-1 duration-150">
                              {idea?.department && (
                                <div className="px-4 py-2.5 text-sm font-medium text-slate-400 bg-slate-50 flex items-center justify-between cursor-not-allowed select-none">
                                  <span>{idea.department}</span>
                                  <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-bold">Current</span>
                                </div>
                              )}
                              {(newDept && newDept === deptSearchQuery
                                ? DEPARTMENTS
                                : DEPARTMENTS.filter(d => d.toLowerCase().includes(deptSearchQuery.toLowerCase()))
                              ).filter(d => d !== idea?.department).map((dept) => (
                                <div
                                  key={dept}
                                  onMouseDown={e => e.preventDefault()}
                                  onClick={() => {
                                    setNewDept(dept);
                                    setDeptSearchQuery(dept);
                                    setShowDeptSearchDropdown(false);
                                  }}
                                  className={`px-4 py-2.5 transition-colors cursor-pointer text-sm font-medium ${newDept === dept ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
                                >
                                  {dept}
                                </div>
                              ))}
                              {!(newDept && newDept === deptSearchQuery) && DEPARTMENTS.filter(d =>
                                d.toLowerCase().includes(deptSearchQuery.toLowerCase()) && d !== idea?.department
                              ).length === 0 && !deptSearchQuery && (
                                  <div className="px-4 py-3 text-sm text-slate-400 italic text-center">
                                    No other departments found.
                                  </div>
                                )}
                              {!(newDept && newDept === deptSearchQuery) && deptSearchQuery && DEPARTMENTS.filter(d =>
                                d.toLowerCase().includes(deptSearchQuery.toLowerCase()) && d !== idea?.department
                              ).length === 0 && (
                                  <div className="px-4 py-3 text-sm text-slate-400 italic text-center">
                                    No matching departments found.
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Reason for Re-routing *</label>
                      <textarea
                        value={changeDeptReason}
                        onChange={e => setChangeDeptReason(e.target.value)}
                        placeholder="Why is this idea being moved to a different department? (min 3 characters)"
                        className="w-full border border-slate-300 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none h-20"
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setIsChangeDeptOpen(false)}
                        className="px-3 py-1.5 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={changeDeptMutation.isPending || !newDept || newDept === idea?.department || !changeDeptReason.trim()}
                        onClick={() => changeDeptMutation.mutate({ department: newDept, reason: changeDeptReason })}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold shadow-sm transition-colors cursor-pointer"
                      >
                        {changeDeptMutation.isPending ? 'Moving...' : 'Move Department'}
                      </button>
                    </div>
                  </div>
                )}

                {activeAction !== 'EVALUATE' ? (
                  <form onSubmit={handleActionSubmit} className="space-y-4 bg-slate-50 p-5 rounded-xl border border-slate-200 shadow-inner">
                    <div className="flex items-start space-x-3 mb-2">
                      <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-100">
                        {activeAction === 'REJECT' || activeAction === 'DUPLICATE' ? <X className="w-5 h-5 text-red-500" /> : (activeAction === 'UNDUPLICATE' || activeAction === 'UNREJECT' || activeAction === 'UNHOLD' || activeAction === 'UNHOLD_EXTERNAL' ? <RefreshCw className="w-5 h-5 text-blue-500" /> : <Clock className="w-5 h-5 text-amber-500" />)}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800">{activeAction === 'REJECT' ? 'Reject Idea' : (activeAction === 'DUPLICATE' ? 'Mark as Duplicate' : (activeAction === 'UNDUPLICATE' ? 'Unmark as Duplicate' : (activeAction === 'UNREJECT' || activeAction === 'UNHOLD' || activeAction === 'UNHOLD_EXTERNAL' ? 'Revert Action' : 'Put Idea on Hold')))}</h4>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          {activeAction === 'REJECT'
                            ? 'Please provide a reason for rejecting this idea.'
                            : (activeAction === 'DUPLICATE' ? 'Please provide a reason or link for marking this as a duplicate.' : (activeAction === 'UNDUPLICATE' || activeAction === 'UNREJECT' || activeAction === 'UNHOLD' || activeAction === 'UNHOLD_EXTERNAL' ? 'Please provide a reason for reverting this action.' : 'Please provide a reason for putting this idea on hold.'))}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">
                        Reason for {activeAction === 'REJECT' ? 'Rejection' : (activeAction === 'DUPLICATE' ? 'Duplicate' : (activeAction === 'UNDUPLICATE' || activeAction === 'UNREJECT' || activeAction === 'UNHOLD' || activeAction === 'UNHOLD_EXTERNAL' ? 'Reverting' : 'Hold'))} *
                      </label>
                      <textarea
                        rows={3}
                        value={actionReason}
                        onChange={(e) => {
                          setActionReason(e.target.value);
                          if (e.target.value.trim().length >= 5) setReviewError('');
                        }}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
                        placeholder={`Enter reason for ${activeAction === 'REJECT' ? 'rejection' : (activeAction === 'DUPLICATE' ? 'marking duplicate' : (activeAction === 'UNDUPLICATE' || activeAction === 'UNREJECT' || activeAction === 'UNHOLD' || activeAction === 'UNHOLD_EXTERNAL' ? 'reverting' : 'hold'))} (min 5 characters)...`}
                      ></textarea>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => { setActiveAction('EVALUATE'); setReviewError(''); }}
                        className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-lg font-medium text-xs transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={reviewMutation.isPending || actionReason.trim().length < 5}
                        className={`text-white px-4 py-2 rounded-lg font-bold text-xs transition-colors cursor-pointer shadow-sm flex items-center space-x-2 disabled:opacity-50 ${activeAction === 'HOLD' ? 'bg-amber-600 hover:bg-amber-700' : (activeAction === 'UNDUPLICATE' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700')}`}
                      >
                        {reviewMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <span>Confirm {activeAction === 'REJECT' ? 'Reject' : (activeAction === 'DUPLICATE' ? 'Duplicate' : (activeAction === 'UNDUPLICATE' || activeAction === 'UNREJECT' || activeAction === 'UNHOLD' || activeAction === 'UNHOLD_EXTERNAL' ? 'Revert' : 'Hold'))}</span>
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleEvalSubmit} className="space-y-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-100 border-b border-slate-200">
                          <tr>
                            <th className="px-4 py-2 font-bold text-slate-600">Criteria</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          <tr>
                            <td className="px-4 py-3 text-slate-700">
                              <label className="flex items-center space-x-3 cursor-pointer group w-max">
                                <div className="relative inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={evalData.savingPotential}
                                    disabled={!isEvalFormEditable}
                                    onChange={(e) => handleCheckboxChange('savingPotential', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 peer-focus:ring-2 peer-focus:ring-indigo-300 peer-disabled:opacity-60 peer-checked:bg-indigo-600 peer-checked:border-indigo-600 border-slate-300 bg-white group-hover:border-indigo-400 shadow-sm">
                                    {evalData.savingPotential && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                  </div>
                                </div>
                                <span className={`font-semibold transition-colors ${!isEvalFormEditable ? 'text-emerald-800' : 'group-hover:text-indigo-600'}`}>1) Saving Potential</span>
                              </label>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-slate-700">
                              <label className="flex items-center space-x-3 cursor-pointer group w-max">
                                <div className="relative inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={evalData.easeOfImplementation}
                                    disabled={!isEvalFormEditable}
                                    onChange={(e) => handleCheckboxChange('easeOfImplementation', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 peer-focus:ring-2 peer-focus:ring-indigo-300 peer-disabled:opacity-60 peer-checked:bg-indigo-600 peer-checked:border-indigo-600 border-slate-300 bg-white group-hover:border-indigo-400 shadow-sm">
                                    {evalData.easeOfImplementation && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                  </div>
                                </div>
                                <span className={`font-semibold transition-colors ${!isEvalFormEditable ? 'text-emerald-800' : 'group-hover:text-indigo-600'}`}>2) Ease of Implementation</span>
                              </label>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-slate-700">
                              <label className="flex items-center space-x-3 cursor-pointer group w-max">
                                <div className="relative inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={evalData.timeOfImplementation}
                                    disabled={!isEvalFormEditable}
                                    onChange={(e) => handleCheckboxChange('timeOfImplementation', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 peer-focus:ring-2 peer-focus:ring-indigo-300 peer-disabled:opacity-60 peer-checked:bg-indigo-600 peer-checked:border-indigo-600 border-slate-300 bg-white group-hover:border-indigo-400 shadow-sm">
                                    {evalData.timeOfImplementation && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                  </div>
                                </div>
                                <span className={`font-semibold transition-colors ${!isEvalFormEditable ? 'text-emerald-800' : 'group-hover:text-indigo-600'}`}>3) Time of Implementation</span>
                              </label>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {evalData.category && (
                      <div>
                        <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Calculated Category</label>
                        <div className="inline-flex items-center justify-center px-4 py-2 bg-indigo-50 text-indigo-700 font-bold rounded-lg border border-indigo-100 shadow-sm text-sm">
                          Category {evalData.category}
                        </div>
                      </div>
                    )}

                    <div className="pt-2">
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Comments *</label>
                      <textarea
                        rows={5}
                        placeholder="Any specific thoughts or notes?"
                        value={evalData.comments}
                        disabled={!isEvalFormEditable}
                        onChange={(e) => {
                          setEvalData(prev => ({ ...prev, comments: e.target.value }));
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        ref={(el) => {
                          if (el) {
                            el.style.height = 'auto';
                            el.style.height = el.scrollHeight + 'px';
                          }
                        }}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:bg-slate-50 disabled:cursor-not-allowed resize-none overflow-hidden"
                      ></textarea>

                      {(existingEvalAttachments.length > 0 || evalAttachments.length > 0) && (
                        <div className="mt-4">
                          <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Attachments</label>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {existingEvalAttachments.map((att: any, idx: number) => (
                              <a
                                key={`existing-${idx}`}
                                href={att.downloadUrl || att.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center space-x-1 hover:bg-emerald-100 transition-colors rounded-lg p-1.5 border border-emerald-100 bg-emerald-50 px-3"
                                title="View File"
                              >
                                <Paperclip className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs text-emerald-800 max-w-[200px] truncate hover:underline cursor-pointer">{att.fileName}</span>
                              </a>
                            ))}
                            {evalAttachments.map((att, idx) => (
                              <a
                                key={`new-${idx}`}
                                href={att.downloadUrl || att.fileUrl || `https://cg-idea-prod-attachments-017820706795.s3.ap-south-1.amazonaws.com/${att.fileKey}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center space-x-1 hover:bg-indigo-100 transition-colors rounded-lg p-1.5 border border-indigo-100 bg-indigo-50 px-3"
                                title="View File"
                              >
                                <Paperclip className="w-4 h-4 text-indigo-500" />
                                <span className="text-xs text-indigo-700 max-w-[200px] truncate hover:underline cursor-pointer">{att.fileName}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {isEvalFormEditable && (
                      <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col items-end space-y-3">
                        <button
                          type="submit"
                          disabled={evalMutation.isPending || reviewMutation.isPending || isUploadingEvalFiles}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs flex items-center justify-center space-x-2 transition-all shadow-md shadow-indigo-600/20 disabled:opacity-50"
                        >
                          {evalMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>{hasEvaluations ? 'Update Evaluation' : 'Submit Evaluation'}</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </form>
                )}
              </div>

              {/* Rejection banner removed from here and placed at the bottom */}
            </>
          )}

          {/* Validation & Accrual Mockup */}
          {((isModerator) || userRole === 'COMMITTEE_HEAD' || userRole === 'ADMIN' || userRole === 'EXTERNAL_COMMITTEE' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'LEADER') && (
            <>
              <div className={`p-6 rounded-xl border shadow-md space-y-4 ${isValidationCompleted ? 'bg-emerald-50/30 border-emerald-300' : (!isValidationEditable ? 'bg-white border-slate-200' : 'bg-white border-indigo-200')}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-xs uppercase font-bold tracking-wider font-display flex items-center space-x-2 ${isValidationCompleted ? 'text-emerald-700' : 'text-indigo-700'}`}>
                    <span>Validation & Accrual {isValidationCompleted && '(Completed)'}</span>
                    {isValidationCompleted && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </h3>
                </div>

                <div className="pt-2">
                  <p className="text-[11px] text-slate-500 font-medium mb-4 italic px-2 py-1.5 bg-yellow-50 text-yellow-700 rounded border border-yellow-100 inline-block">
                    * for every validated ideas below things to be defined in 7 days
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">
                        1. Current Cost <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. ₹5,00,000"
                        value={currentCost}
                        disabled={!isValidationEditable}
                        onChange={(e) => {
                          setCurrentCostError('');
                          handleCurrencyChange(setCurrentCost)(e);
                        }}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700"
                      />
                      {currentCostError && (
                        <p className="text-red-500 text-[11px] font-semibold mt-1">{currentCostError}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">2. Propose Solution</label>
                      <textarea rows={3} value={proposeSolution} disabled={!isValidationEditable} onChange={e => setProposeSolution(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700" placeholder="Describe the proposed solution"></textarea>
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">3. Saving Type</label>
                      <select value={savingType} disabled={!isValidationEditable} onChange={e => setSavingType(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700">
                        <option value="">-- Select Option --</option>
                        <option value="one-time">One Time Saving</option>
                        <option value="recurring">Recurring Saving</option>
                      </select>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="block text-[10px] text-slate-500 font-bold uppercase">
                          4. Annual Savings <span className="text-red-500">*</span>
                        </label>
                        {originalSavings && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-medium border border-slate-200">
                            Original: {originalSavings}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="e.g. ₹10,00,000"
                        value={annualSavings}
                        disabled={!isValidationEditable}
                        onChange={(e) => {
                          setAnnualSavingsError('');
                          handleCurrencyChange(setAnnualSavings)(e);
                        }}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700"
                      />
                      {annualSavingsError && (
                        <p className="text-red-500 text-[11px] font-semibold mt-1">{annualSavingsError}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">5. One Time Investment (Any)</label>
                      <input
                        type="text"
                        placeholder="e.g. ₹1,00,000"
                        value={oneTimeInvestment}
                        disabled={!isValidationEditable}
                        onChange={handleCurrencyChange(setOneTimeInvestment)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700"
                      />
                    </div>

                    <div className="relative" ref={ownerDropdownRef}>
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">6. Owner</label>
                      <input
                        type="text"
                        placeholder="Search by name, ID, or email..."
                        value={ownerSearchInput}
                        disabled={!isValidationEditable}
                        onChange={(e) => {
                          setOwnerSearchInput(e.target.value);
                          setShowOwnerDropdown(true);
                        }}
                        onFocus={() => setShowOwnerDropdown(true)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700"
                      />
                      {isSearchingOwner && (
                        <span className="absolute right-3 top-7">
                          <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                        </span>
                      )}
                      {showOwnerDropdown && ownerSuggestions.length > 0 && (
                        <div className="absolute z-50 left-0 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-[200px] overflow-y-auto p-1.5 flex flex-col gap-1 animate-in slide-in-from-top-2 duration-150">
                          {ownerSuggestions.map((emp) => (
                            <div
                              key={emp.id}
                              onClick={() => {
                                setOwnerSearchInput(emp.name);
                                setShowOwnerDropdown(false);
                              }}
                              className="px-3 py-2.5 hover:bg-indigo-50/80 rounded-lg cursor-pointer transition-all flex flex-col group border border-transparent hover:border-indigo-100"
                            >
                              <span className="text-sm font-bold text-slate-800 group-hover:text-indigo-700 transition-colors">{emp.name} {emp.cgId ? `(${emp.cgId})` : ''}</span>
                              <span className="text-xs text-slate-500 mt-0.5 truncate">{emp.department ? `${emp.department} • ` : ''}{emp.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">7. Implementation Timeline</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                          <Calendar className="w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                        </div>
                        <DatePicker
                          selected={implementationTimeline && /^\d{4}-\d{2}-\d{2}$/.test(implementationTimeline) ? new Date(implementationTimeline) : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const tzOffset = date.getTimezoneOffset() * 60000;
                              const localISOTime = (new Date(date.getTime() - tzOffset)).toISOString().split('T')[0];
                              setImplementationTimeline(localISOTime);
                            } else {
                              setImplementationTimeline('');
                            }
                          }}
                          disabled={!isValidationEditable}
                          minDate={new Date()}
                          dateFormat="MMM d, yyyy"
                          placeholderText="Select target date"
                          className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700 cursor-pointer shadow-sm hover:border-slate-400"
                          wrapperClassName="w-full"
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </div>
                    </div>

                    <div className="col-span-1 sm:col-span-2 mt-2">
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">8. Risk</label>
                      <textarea rows={3} value={risk} disabled={!isValidationEditable} onChange={e => setRisk(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700" placeholder="Identify any risks involved"></textarea>
                    </div>

                    <div className="col-span-1 sm:col-span-2 mt-2">
                      <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">9. Notes</label>
                      <textarea rows={3} value={notes} disabled={!isValidationEditable} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all disabled:opacity-100 disabled:bg-slate-50/50 disabled:font-semibold disabled:text-slate-700" placeholder="Enter any additional notes or comments"></textarea>
                    </div>

                    {isValidationEditable ? (
                      <div className="col-span-1 sm:col-span-2 mt-2">
                        <label className="flex items-center space-x-2 text-xs text-indigo-600 font-semibold cursor-pointer hover:text-indigo-700 transition-colors w-max">
                          <Paperclip className="w-4 h-4" />
                          <span>Attach Notes/Files</span>
                          <input
                            type="file"
                            multiple
                            onChange={handleValFileChange}
                            className="hidden"
                            disabled={isUploadingValFiles}
                          />
                        </label>
                        {valUploadError && <p className="text-red-500 text-[10px] mt-1">{valUploadError}</p>}
                        {isUploadingValFiles && (
                          <div className="flex items-center space-x-1.5 mt-2">
                            <Loader2 className="w-3 h-3 animate-spin text-indigo-500" />
                            <span className="text-[10px] text-slate-500">Uploading files...</span>
                          </div>
                        )}
                        {(existingValidationAttachments.length > 0 || valAttachments.length > 0) && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {existingValidationAttachments.map((att: any, idx: number) => (
                              <div key={`existing-${idx}`} className="flex items-center space-x-1 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100">
                                <a
                                  href={att.downloadUrl || att.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center space-x-1 hover:bg-emerald-100 transition-colors rounded p-0.5"
                                  title="View File"
                                >
                                  <Paperclip className="w-4 h-4 text-emerald-600" />
                                  <span className="text-xs text-emerald-800 max-w-[200px] truncate hover:underline cursor-pointer">{att.fileName}</span>
                                </a>
                              </div>
                            ))}
                            {valAttachments.map((att, idx) => (
                              <div key={`new-${idx}`} className="flex items-center space-x-1 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100">
                                <a
                                  href={att.downloadUrl || att.fileUrl || `https://cg-idea-prod-attachments-017820706795.s3.ap-south-1.amazonaws.com/${att.fileKey}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center space-x-1 hover:bg-indigo-100 transition-colors rounded p-0.5"
                                  title="View File"
                                >
                                  <Paperclip className="w-4 h-4 text-indigo-500" />
                                  <span className="text-xs text-indigo-700 max-w-[200px] truncate hover:underline cursor-pointer">{att.fileName}</span>
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setValAttachments(prev => prev.filter((_, i) => i !== idx))}
                                  className="ml-1.5 text-slate-400 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      (existingValidationAttachments.length > 0 || valAttachments.length > 0) && (
                        <div className="col-span-1 sm:col-span-2 mt-2">
                          <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Attachments</label>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {existingValidationAttachments.map((att: any, idx: number) => (
                              <a
                                key={`existing-${idx}`}
                                href={att.downloadUrl || att.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center space-x-1 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100 hover:bg-emerald-100 transition-colors"
                              >
                                <Paperclip className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs text-emerald-800 max-w-[200px] truncate">{att.fileName}</span>
                              </a>
                            ))}
                            {valAttachments.map((att, idx) => (
                              <a
                                key={`new-${idx}`}
                                href={att.downloadUrl || att.fileUrl || `https://cg-idea-prod-attachments-017820706795.s3.ap-south-1.amazonaws.com/${att.fileKey}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center space-x-1 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100 hover:bg-indigo-100 transition-colors"
                              >
                                <Paperclip className="w-4 h-4 text-indigo-500" />
                                <span className="text-xs text-indigo-700 max-w-[200px] truncate">{att.fileName}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col items-end space-y-3">
                    {committeeStatus === 'waiting' && (
                      <div className="flex items-center space-x-2 text-[11px] font-bold text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Sending saving ideas to external committee for approval and accrual to DOM680...</span>
                      </div>
                    )}
                    {(idea.status === 'SUBMITTED' || idea.status === 'UNDER_REVIEW') && (userRole === 'COMMITTEE_MEMBER' || userRole === 'COMMITTEE_HEAD' || userRole === 'LEADER' || userRole === 'ADMIN') ? (
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            let hasError = false;
                            if (!currentCost || currentCost.toString().trim() === '') {
                              setCurrentCostError('Current Cost is required.');
                              hasError = true;
                            } else {
                              setCurrentCostError('');
                            }
                            if (!annualSavings || annualSavings.toString().trim() === '') {
                              setAnnualSavingsError('Annual Savings is required.');
                              hasError = true;
                            } else {
                              setAnnualSavingsError('');
                            }
                            if (hasError) {
                              setValError('');
                              return;
                            }
                            setValError('');
                            setShowReviewConfirmModal(true);
                          }}
                          disabled={!isValidationEditable || reviewMutation.isPending || isUploadingValFiles}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs flex items-center justify-center space-x-2 transition-all shadow-md shadow-indigo-600/20 disabled:opacity-50"
                        >
                          {reviewMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Review to Approve</span>
                            </>
                          )}
                        </button>
                      </div>
                    ) : isValidationEditable ? (
                      <button
                        type="button"
                        onClick={() => {
                          let hasError = false;
                          if (!currentCost || currentCost.toString().trim() === '') {
                            setCurrentCostError('Current Cost is required.');
                            hasError = true;
                          } else {
                            setCurrentCostError('');
                          }
                          if (!annualSavings || annualSavings.toString().trim() === '') {
                            setAnnualSavingsError('Annual Savings is required.');
                            hasError = true;
                          } else {
                            setAnnualSavingsError('');
                          }
                          if (hasError) {
                            setValError('');
                            return;
                          }

                          const updatedFields = [];
                          const formatVal = (v: any) => (v && v.toString().trim() !== '') ? v.toString() : 'None';

                          const checkField = (name: string, oldV: any, newV: any) => {
                            const o = formatVal(oldV);
                            const n = formatVal(newV);
                            if (o !== n) updatedFields.push(`${name}: '${o}' ➔ '${n}'`);
                          };

                          checkField('Current Cost', idea.validation?.currentCost, currentCost);
                          checkField('Propose Solution', idea.validation?.proposeSolution, proposeSolution);
                          checkField('Saving Type', idea.validation?.savingType, savingType);
                          checkField('Annual Savings', idea.validation?.annualSavings, annualSavings);
                          checkField('One Time Investment', idea.validation?.oneTimeInvestment, oneTimeInvestment);
                          checkField('Owner', idea.validation?.owner, ownerSearchInput);
                          checkField('Timeline', idea.validation?.implementationTimeline, implementationTimeline);
                          checkField('Risk', idea.validation?.risk, risk);
                          checkField('Notes', idea.validation?.notes, notes);

                          const attachmentsChanged = valAttachments.length > 0;
                          if (attachmentsChanged) updatedFields.push('Attachments updated');

                          if (updatedFields.length === 0) {
                            setValError('No changes detected to update.');
                            return;
                          }
                          setValError('');

                          const comment = updatedFields.length > 0
                            ? `Validation details updated:\n${updatedFields.map(f => `• ${f}`).join('\n')}`
                            : 'Validation details updated';

                          reviewMutation.mutate({
                            status: idea.status,
                            comment,
                            validation: {
                              currentCost,
                              proposeSolution,
                              savingType,
                              annualSavings,
                              oneTimeInvestment,
                              owner: ownerSearchInput,
                              implementationTimeline,
                              risk,
                              notes,
                              attachments: valAttachments,
                              updatedFieldsStr: comment
                            }
                          });
                        }}
                        disabled={reviewMutation.isPending || isUploadingValFiles}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs flex items-center justify-center space-x-2 transition-all shadow-md shadow-indigo-600/20 disabled:opacity-50"
                      >
                        {reviewMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Update Validation</span>
                          </>
                        )}
                      </button>
                    ) : null}
                  </div>
                  {valError && <p className="text-red-500 text-xs font-semibold mt-2 text-right">{valError}</p>}
                </div>
              </div>
            </>
          )
          }


          {/* === STATUS ACTION BANNERS (LATEST STEPS) === */}


          {/* Department Head Final Approval Card */}
          {
            idea.status === 'PENDING_HEAD_APPROVAL' && userRole === 'COMMITTEE_HEAD' && !isMisApproved && (
              <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-200 shadow-md flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-emerald-800 tracking-wide">Pending Final Approval</h3>
                    <p className="text-xs text-emerald-700 mt-1 font-medium">This idea has been validated and requires your final approval.</p>
                  </div>
                  {headAction === 'NONE' && (
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setHeadAction('REJECT');
                          setHeadActionError('');
                          setHeadActionReason('');
                        }}
                        disabled={reviewMutation.isPending}
                        className="bg-white text-red-600 hover:bg-red-50 border border-red-200 font-bold py-2 px-5 rounded-xl text-xs transition-colors shadow-sm disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setHeadAction('APPROVE');
                          setHeadActionError('');
                          setHeadActionReason('');
                        }}
                        disabled={reviewMutation.isPending}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-5 rounded-xl text-xs transition-colors shadow-sm disabled:opacity-50"
                      >
                        Final Approve
                      </button>
                    </div>
                  )}
                </div>

                {headAction !== 'NONE' && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    if (!headActionReason.trim()) {
                      setHeadActionError('Please provide a reason.');
                      return;
                    }

                    reviewMutation.mutate({
                      status: headAction === 'REJECT' ? 'REJECTED' : 'PENDING_EXTERNAL_APPROVAL',
                      comment: headActionReason
                    });
                  }} className="mt-4 pt-4 border-t border-emerald-200/60 animate-in fade-in duration-200">

                    <label className="block text-[10px] font-bold text-emerald-800 uppercase tracking-wide mb-2">
                      Reason for {headAction === 'REJECT' ? 'Rejection' : 'Approval'} *
                    </label>
                    <textarea
                      rows={3}
                      value={headActionReason}
                      onChange={(e) => setHeadActionReason(e.target.value)}
                      placeholder={`Enter reason for ${headAction === 'REJECT' ? 'rejection' : 'approval'}...`}
                      className="w-full px-3 py-2 bg-white border border-emerald-200 rounded-lg text-emerald-900 text-xs focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                    ></textarea>
                    {headActionError && <p className="text-red-500 text-[10px] mt-1 font-semibold">{headActionError}</p>}

                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setHeadAction('NONE');
                          setHeadActionError('');
                        }}
                        className="px-4 py-2 bg-white border border-emerald-200 hover:bg-emerald-100 text-emerald-800 rounded-lg font-medium text-xs transition-colors cursor-pointer shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={reviewMutation.isPending}
                        className={`px-4 py-2 text-white rounded-lg font-bold text-xs transition-colors cursor-pointer shadow-sm flex items-center space-x-2 ${headAction === 'REJECT' ? 'bg-red-600 hover:bg-red-700 shadow-red-600/20' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'}`}
                      >
                        {reviewMutation.isPending ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <span>Confirm {headAction === 'REJECT' ? 'Reject' : 'Approve'}</span>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )
          }

          {/* External Committee Final Approval Card */}
          {
            idea.status === 'PENDING_EXTERNAL_APPROVAL' && userRole === 'EXTERNAL_COMMITTEE' && !isMisApproved && (
              <div className="bg-orange-50 p-6 rounded-xl border border-orange-200 shadow-md flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-orange-800 tracking-wide">External Committee Review</h3>
                    <p className="text-xs text-orange-700 mt-1 font-medium">sending saving ideas to external committee for a approval and accruel to DOM680</p>
                  </div>
                  {extAction === 'NONE' && (
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setExtAction('REJECT');
                          setExtActionError('');
                          setExtActionReason('');
                        }}
                        disabled={reviewMutation.isPending}
                        className="bg-white text-red-600 hover:bg-red-50 border border-red-200 font-bold py-2 px-5 rounded-xl text-xs transition-colors shadow-sm disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setExtAction('HOLD');
                          setExtActionError('');
                          setExtActionReason('');
                        }}
                        disabled={reviewMutation.isPending}
                        className="bg-white text-amber-600 hover:bg-amber-50 border border-amber-200 font-bold py-2 px-5 rounded-xl text-xs transition-colors shadow-sm disabled:opacity-50"
                      >
                        Hold
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setExtAction('APPROVE');
                          setExtActionError('');
                          setExtActionReason('');
                        }}
                        disabled={reviewMutation.isPending}
                        className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-5 rounded-xl text-xs transition-colors shadow-sm disabled:opacity-50"
                      >
                        Approve
                      </button>
                    </div>
                  )}
                </div>

                {extAction !== 'NONE' && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    if (!extActionReason.trim()) {
                      setExtActionError('Please provide a reason.');
                      return;
                    }

                    if (extAction === 'APPROVE' && isRevisingSavings && !revisedSavings.trim()) {
                      setExtActionError('Please provide the revised annual savings amount.');
                      return;
                    }

                    let finalComment = extActionReason;
                    let validationPayload: any = undefined;

                    if (extAction === 'APPROVE' && isRevisingSavings) {
                      finalComment = `[Annual Savings revised from ${annualSavings || 'N/A'} to ${revisedSavings}] ${extActionReason}`;
                      validationPayload = { annualSavings: revisedSavings };
                    }

                    reviewMutation.mutate({
                      status: extAction === 'REJECT' ? 'REJECTED' : (extAction === 'HOLD' ? 'MORE_INFO_REQUESTED' : 'APPROVED'),
                      comment: finalComment,
                      ...(validationPayload && { validation: validationPayload })
                    });
                  }} className="mt-4 pt-4 border-t border-orange-200/60 animate-in fade-in duration-200">

                    {extAction === 'APPROVE' && (
                      <div className="mb-4 bg-white p-4 rounded-xl border border-orange-200 shadow-sm">
                        <label className="flex items-center space-x-2 text-xs font-bold text-orange-800 cursor-pointer w-max">
                          <input
                            type="checkbox"
                            checked={isRevisingSavings}
                            onChange={(e) => {
                              setIsRevisingSavings(e.target.checked);
                              if (e.target.checked && !revisedSavings) {
                                setRevisedSavings(annualSavings || '');
                              }
                            }}
                            className="rounded border-orange-400 text-orange-600 focus:ring-orange-500 w-4 h-4 cursor-pointer"
                          />
                          <span>Revise Annual Savings?</span>
                        </label>

                        {isRevisingSavings && (
                          <div className="mt-4 animate-in fade-in slide-in-from-top-1 duration-200">
                            <div className="flex justify-between items-center mb-1.5">
                              <label className="block text-[10px] font-bold text-orange-700 uppercase tracking-wide">
                                Revised Annual Savings (₹) *
                              </label>
                              <span className="text-[10px] text-orange-600/80 font-semibold bg-orange-100/50 px-2 py-0.5 rounded">
                                Old: {annualSavings || 'N/A'}
                              </span>
                            </div>
                            <input
                              type="text"
                              value={revisedSavings}
                              onChange={handleCurrencyChange(setRevisedSavings)}
                              placeholder="e.g. ₹1,00,000"
                              className="w-full px-3 py-2 bg-white border border-orange-300 rounded-lg text-orange-900 text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <label className="block text-[10px] font-bold text-orange-800 uppercase tracking-wide mb-2">
                      Reason for {extAction === 'REJECT' ? 'Rejection' : (extAction === 'HOLD' ? 'Hold' : (isRevisingSavings ? 'Approval & Revision' : 'Approval'))} *
                    </label>
                    <textarea
                      rows={3}
                      value={extActionReason}
                      onChange={(e) => setExtActionReason(e.target.value)}
                      placeholder={`Enter reason for ${extAction === 'REJECT' ? 'rejection' : (extAction === 'HOLD' ? 'hold' : 'approval')}...`}
                      className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg text-orange-900 text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                    ></textarea>
                    {extActionError && <p className="text-red-500 text-[10px] mt-1 font-semibold">{extActionError}</p>}

                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setExtAction('NONE');
                          setExtActionError('');
                        }}
                        className="px-4 py-2 bg-white border border-orange-200 hover:bg-orange-100 text-orange-800 rounded-lg font-medium text-xs transition-colors cursor-pointer shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={reviewMutation.isPending}
                        className={`px-4 py-2 text-white rounded-lg font-bold text-xs transition-colors cursor-pointer shadow-sm flex items-center space-x-2 ${extAction === 'REJECT' ? 'bg-red-600 hover:bg-red-700 shadow-red-600/20' : (extAction === 'HOLD' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-600/20' : 'bg-orange-600 hover:bg-orange-700 shadow-orange-600/20')}`}
                      >
                        {reviewMutation.isPending ? (
                          <span>Processing...</span>
                        ) : (
                          <span>Confirm {extAction === 'REJECT' ? 'Reject' : (extAction === 'HOLD' ? 'Hold' : 'Approve')}</span>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )
          }

          {/* Waiting for Department Head Approval Banner */}
          {
            idea.status === 'PENDING_HEAD_APPROVAL' && userRole !== 'COMMITTEE_HEAD' && (userRole !== 'EMPLOYEE' || isModerator) && (
              <div className="w-full bg-amber-50 p-6 rounded-xl border border-amber-200 shadow-md flex items-center justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <Loader2 className="w-5 h-5 text-amber-600 animate-spin mt-0.5" />
                  <div>
                    <h3 className="text-sm font-bold text-amber-800 tracking-wide">Waiting for Department Head Approval</h3>
                    <p className="text-xs text-amber-700 mt-1 font-medium">
                      This idea is currently pending final approval from{' '}
                      <span className="font-bold">{idea.committeeHead?.name || 'Department Head'}</span>
                      {idea.committeeHead?.email && ` (${idea.committeeHead.email})`}.
                    </p>
                  </div>
                </div>
              </div>
            )
          }

          {/* Email Confirmation Section */}
          {
            (userRole === 'COMMITTEE_HEAD' || userRole === 'ADMIN') &&
            (['PENDING_EXTERNAL_APPROVAL', 'APPROVED', 'EXECUTION_APPROVED', 'PENDING_EXECUTION_APPROVAL'].includes(idea.status) || isMisDuplicate) && (
              <div className="w-full bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-md relative mt-6 mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-slate-200 p-2 rounded-lg">
                    <Mail className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 tracking-wide uppercase">Email Confirmation</h3>
                    <p className="text-xs text-slate-500 mt-0.5 font-medium">Head approval details and confirmation documents</p>
                  </div>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                        Annual Savings Amount (₹) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={emailApprovedAmount}
                        readOnly={isMisApproved}
                        onChange={(e) => {
                          setEmailError('');
                          handleCurrencyChange(setEmailApprovedAmount)(e);
                        }}
                        placeholder="e.g. ₹1,00,000"
                        className={`w-full px-3 py-2 bg-white border ${emailError ? 'border-red-500 ring-1 ring-red-500' : 'border-slate-300'} rounded-lg text-slate-900 text-xs focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none transition-all ${isMisApproved ? 'bg-slate-50' : ''}`}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                        Confirmation Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={emailConfirmationDate}
                        readOnly={isMisApproved}
                        onChange={(e) => setEmailConfirmationDate(e.target.value)}
                        className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-xs focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none transition-all font-medium ${isMisApproved ? 'bg-slate-50' : 'cursor-pointer'}`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                      Reason
                    </label>
                    <textarea
                      rows={3}
                      value={emailReason}
                      readOnly={isMisApproved}
                      onChange={(e) => setEmailReason(e.target.value)}
                      placeholder="Enter reason or additional notes..."
                      className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-xs focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none transition-all ${isMisApproved ? 'bg-slate-50' : ''}`}
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                      Attachments (Optional)
                    </label>
                    <div className="flex flex-col space-y-3">
                      {!isMisApproved && (
                      <label className={`flex items-center justify-center space-x-2 w-full sm:w-max px-4 py-2 border border-slate-300 border-dashed rounded-lg text-slate-600 bg-slate-50 hover:bg-slate-100 hover:text-slate-700 transition-colors cursor-pointer ${isUploadingEmailFiles ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {isUploadingEmailFiles ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        <span className="text-xs font-semibold">{isUploadingEmailFiles ? 'Uploading...' : 'Upload File'}</span>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          className="hidden"
                          onChange={handleEmailFileUpload}
                          disabled={isUploadingEmailFiles}
                        />
                      </label>
                      )}

                      {emailUploadError && <p className="text-[10px] text-red-500 font-semibold">{emailUploadError}</p>}

                      {emailAttachments.length > 0 && (
                        <div className="flex flex-col space-y-2 mt-2">
                          {emailAttachments.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg text-xs">
                              <div className="flex items-center space-x-2 overflow-hidden">
                                <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                <span className="font-medium text-slate-700 truncate">{file.fileName}</span>
                                <span className="text-slate-400">({(file.fileSize / 1024 / 1024).toFixed(2)} MB)</span>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                {(file.downloadUrl || file.fileUrl) && (
                                  <a
                                    href={file.downloadUrl || file.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-blue-600 transition-colors"
                                    title="View File"
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                {!isMisApproved && (
                                <button
                                  type="button"
                                  onClick={() => setEmailAttachments(prev => prev.filter((_, i) => i !== idx))}
                                  className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {emailError && <p className="text-red-500 text-[10px] mt-1 font-semibold">{emailError}</p>}

                  {!isMisApproved && (
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={emailConfirmationMutation.isPending || isUploadingEmailFiles}
                      className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-bold text-xs transition-colors shadow-sm disabled:opacity-50 flex items-center space-x-2"
                    >
                      {emailConfirmationMutation.isPending ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-3.5 h-3.5" />
                          <span>Save Confirmation</span>
                        </>
                      )}
                    </button>
                  </div>
                  )}
                </form>

                {/* Success Toast for Email */}
                {showEmailToast && (
                  <div className="absolute top-4 right-4 bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs px-3 py-2 rounded shadow-sm flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
                    <Check className="w-3.5 h-3.5" />
                    <span className="font-medium">Saved successfully!</span>
                  </div>
                )}
              </div>
            )
          }

          {/* Waiting for External Committee Approval Banner */}
          {
            idea.status === 'PENDING_EXTERNAL_APPROVAL' && userRole !== 'EXTERNAL_COMMITTEE' && (userRole !== 'EMPLOYEE' || isModerator) && (
              <div className="w-full bg-orange-50 p-6 rounded-xl border border-orange-200 shadow-md flex items-center justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <Loader2 className="w-5 h-5 text-orange-600 animate-spin mt-0.5" />
                  <div>
                    <h3 className="text-sm font-bold text-orange-800 tracking-wide">Waiting for External Committee Approval</h3>
                    <p className="text-xs text-orange-700 mt-1 font-medium">
                      This idea is currently pending external validation.
                    </p>
                  </div>
                </div>
              </div>
            )
          }

          {/* Validation Approved Banner (Final) */}
          {
            idea.status === 'APPROVED' && (
              <div className="w-full bg-emerald-50 p-6 rounded-xl border border-emerald-200 shadow-md flex items-center justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-bold text-emerald-800 tracking-wide">Idea Fully Approved</h3>
                    <p className="text-xs text-emerald-700 mt-1 font-medium">
                      This idea has passed all committee approvals and is ready.
                    </p>
                  </div>
                </div>
              </div>
            )
          }

          {/* External Hold Banner */}
          {idea.status === 'MORE_INFO_REQUESTED' && isExternalHold && (userRole !== 'EMPLOYEE' || isModerator) && (
            <div className="w-full bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-md mt-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
              {activeAction === 'UNHOLD_EXTERNAL' ? (
                <form onSubmit={handleActionSubmit} className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <label className="block text-xs font-bold text-orange-900 mb-1">
                    Reason for Unholding <span className="text-orange-500">*</span> (min 5 chars)
                  </label>
                  <textarea
                    rows={3}
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Enter reason here..."
                    className="w-full px-3 py-2 bg-white border border-orange-300 rounded-lg text-orange-900 text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
                  />
                  {reviewError && <p className="text-red-500 text-[10px] font-semibold">{reviewError}</p>}
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => { setActiveAction('EVALUATE'); setReviewError(''); }}
                      className="px-4 py-2 border border-orange-200 hover:bg-orange-100 text-orange-800 rounded-lg font-medium text-xs transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={reviewMutation.isPending || actionReason.trim().length < 5}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-bold text-xs transition-colors cursor-pointer shadow-sm flex items-center space-x-2 disabled:opacity-50"
                    >
                      {reviewMutation.isPending ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Processing...</span></>
                      ) : (
                        <span>Confirm Unhold</span>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 p-2 rounded-lg shrink-0">
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-orange-900 tracking-wide">Idea is on Hold (External Committee)</h3>
                      <p className="text-xs text-orange-800 mt-1 font-medium leading-relaxed">
                        <span className="font-bold">Reason:</span> {holdReason}
                      </p>
                    </div>
                  </div>
                  {userRole === 'EXTERNAL_COMMITTEE' && !isMisApproved && (
                    <button
                      type="button"
                      onClick={() => { setActiveAction('UNHOLD_EXTERNAL'); setActionReason(''); setReviewError(''); }}
                      className="bg-orange-500 text-white hover:bg-orange-600 border border-orange-600 font-bold py-2 px-5 rounded-xl text-xs transition-colors shadow-sm whitespace-nowrap shrink-0"
                    >
                      Unhold Idea
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Rejection Banner */}
          {idea.status === 'REJECTED' && (
            <div className="w-full bg-red-50 border border-red-200 rounded-xl p-6 shadow-md mt-4">
              {activeAction === 'UNREJECT' ? (
                <form onSubmit={handleActionSubmit} className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <label className="block text-xs font-bold text-red-900 mb-1">
                    Reason for Unrejecting <span className="text-red-500">*</span> (min 5 chars)
                  </label>
                  <textarea
                    rows={3}
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Enter reason here..."
                    className="w-full px-3 py-2 bg-white border border-red-300 rounded-lg text-red-900 text-xs focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all"
                  />
                  {reviewError && <p className="text-red-500 text-[10px] font-semibold">{reviewError}</p>}
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => { setActiveAction('EVALUATE'); setReviewError(''); }}
                      className="px-4 py-2 border border-red-200 hover:bg-red-100 text-red-800 rounded-lg font-medium text-xs transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={reviewMutation.isPending || actionReason.trim().length < 5}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold text-xs transition-colors cursor-pointer shadow-sm flex items-center space-x-2 disabled:opacity-50"
                    >
                      {reviewMutation.isPending ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Processing...</span></>
                      ) : (
                        <span>Confirm Unreject</span>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-3">
                    <X className="w-6 h-6 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-bold text-red-800 tracking-wide">This idea was Rejected</h3>
                      {idea.timelines?.slice().reverse().find((t: any) => t.statusTo === 'REJECTED')?.comment && (
                        <p className="text-xs text-slate-600 mt-1 italic font-medium">
                          "{idea.timelines.slice().reverse().find((t: any) => t.statusTo === 'REJECTED')?.comment}"
                        </p>
                      )}
                    </div>
                  </div>
                  {canUnreject() && (
                    <button
                      type="button"
                      onClick={handleUnreject}
                      disabled={reviewMutation.isPending}
                      className="ml-4 bg-rose-600 hover:bg-rose-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs uppercase tracking-wider transition-colors shadow-md shadow-rose-600/20 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      Unreject
                    </button>
                  )}
                </div>
              )}
            </div>
          )}


        </div >

        {/* Sidebar Info/Timeline */}
        < div className="space-y-6" >
          {/* Submitter Metadata */}
          {/* {userRole === 'ADMIN' && ( */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider">Idea Submitter</h3>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center font-display text-blue-700 font-bold">
                {idea.employee.name.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{idea.employee.name}</p>
                <p className="text-xs text-slate-500 truncate">{idea.employee.email}</p>
              </div>
            </div>

            <hr className="border-slate-100" />

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Employee ID</span>
                <span className="text-slate-800 font-semibold">{idea.employee.mpId || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Department</span>
                <span className="text-slate-800 font-semibold">{idea.employee.department || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Designation</span>
                <span className="text-slate-800 font-semibold">{idea.employee.designation || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 font-medium">Mobile Number</span>
                <span className="text-slate-800 font-semibold">{idea.employee.mobile || 'N/A'}</span>
              </div>
            </div>
          </div>
          {/* )} */}

          {/* Committee Head contact — My Ideas (employee) view only; hidden on evaluate views */}
          {userRole === 'EMPLOYEE' && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <p className="text-xs text-blue-700 font-semibold leading-relaxed">
              For any issues related to this idea, please contact the committee head below.
            </p>

            {idea.committeeHead ? (
              <>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{idea.committeeHead.name}</p>
                  <p className="text-xs text-slate-500 truncate">{idea.committeeHead.email}</p>
                </div>

                <hr className="border-slate-100" />

                <div className="space-y-2.5 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium">Department</span>
                    <span className="text-slate-800 font-semibold">{idea.committeeHead.department || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium">Mobile Number</span>
                    <span className="text-slate-800 font-semibold">{idea.committeeHead.mobile || 'N/A'}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-slate-400 font-medium">No committee head mapped for this department.</p>
            )}
          </div>
          )}

          {/* Admin Moderation Action Card (Only for APPROVAL_TEAM now) */}
          {
            userRole === 'APPROVAL_TEAM' && (idea.status === 'SUBMITTED' || idea.status === 'MORE_INFO_REQUESTED') && (
              <div className="bg-white p-6 rounded-xl border border-blue-200 shadow-md space-y-4">
                <h3 className="text-xs uppercase font-bold text-blue-700 tracking-wider font-display">Evaluate Proposal</h3>

                {reviewError && <p className="text-red-500 text-xs font-semibold">{reviewError}</p>}

                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setReviewStatus('APPROVED')}
                      className={`py-2 px-1 text-[10px] font-bold rounded-lg border flex flex-col items-center justify-center space-y-1 transition-all cursor-pointer ${reviewStatus === 'APPROVED'
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-emerald-300 hover:bg-emerald-50/50'
                        }`}
                    >
                      <Check className="w-4 h-4" />
                      <span>Approve</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setReviewStatus('REJECTED')}
                      className={`py-2 px-1 text-[10px] font-bold rounded-lg border flex flex-col items-center justify-center space-y-1 transition-all cursor-pointer ${reviewStatus === 'REJECTED'
                        ? 'bg-red-50 border-red-500 text-red-700 shadow-sm'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-red-300 hover:bg-red-50/50'
                        }`}
                    >
                      <X className="w-4 h-4" />
                      <span>Reject</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setReviewStatus('MORE_INFO_REQUESTED')}
                      className={`py-2 px-1 text-[9px] font-bold rounded-lg border flex flex-col items-center justify-center space-y-1 transition-all cursor-pointer ${reviewStatus === 'MORE_INFO_REQUESTED'
                        ? 'bg-amber-50 border-amber-500 text-amber-700 shadow-sm'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-amber-300 hover:bg-amber-50/50'
                        }`}
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>Hold</span>
                    </button>
                  </div>

                  <div>
                    <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Reviewer Comments *</label>
                    <textarea
                      rows={3}
                      placeholder="Enter review decision rationale..."
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={reviewMutation.isPending || !reviewStatus || reviewComment.trim().length < 5}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-xl text-xs flex items-center justify-center space-x-1.5 transition-all cursor-pointer disabled:opacity-50 shadow-md shadow-blue-600/20"
                  >
                    {reviewMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <span>Submit Decision</span>
                    )}
                  </button>
                </form>
              </div>
            )
          }

          {/* Past Evaluations Card */}
          {
            evaluations && evaluations.length > 0 && (
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider">Past Evaluations</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {evaluations.map((ev: any) => (
                    <div key={ev.id} className="bg-slate-50 border border-slate-100 p-3 rounded-lg text-xs space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-700">{ev.evaluator?.name}</span>
                        <span className="font-bold text-indigo-600 px-2 py-0.5 bg-indigo-50 rounded border border-indigo-100">Category {ev.category}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          }

          {/* Status Tracker */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider">Submission Status</h3>
              <button
                onClick={() => setShowFullLogModal(true)}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors flex items-center space-x-1"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>View Log</span>
              </button>
            </div>

            <div className="flex items-center space-x-3.5 mb-5">
              <span className={`text-sm uppercase font-bold tracking-wider px-3.5 py-1.5 rounded-full ${displayColor}`}>
                {displayStatus}
              </span>
            </div>

            <div className="space-y-6 border-l-2 border-slate-100 pl-6 relative ml-2 mt-4">
              {finalTimeline}
            </div>
          </div>

          {/* Modal: Full Log */}
          {showFullLogModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
              <div className="bg-slate-50 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
                <div className="p-6 bg-white border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 font-display">Detailed Idea Log</h3>
                      <p className="text-xs text-slate-500 font-medium">Complete history of updates and approvals</p>
                    </div>
                  </div>
                  <button onClick={() => setShowFullLogModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors cursor-pointer">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-8 overflow-y-auto flex-1">
                  <div className="space-y-6 border-l-2 border-slate-200 pl-6 relative ml-2">
                    {finalTimeline}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Review Confirm */}
          {showReviewConfirmModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Send className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2 font-display">Send for Final Approval?</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6">
                    Are you sure you want to send this idea to the Department Head for final approval? You won't be able to edit the validation details once submitted.
                  </p>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowReviewConfirmModal(false)}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        let hasError = false;
                        if (!currentCost || currentCost.toString().trim() === '') {
                          setCurrentCostError('Current Cost is required.');
                          hasError = true;
                        } else {
                          setCurrentCostError('');
                        }
                        if (!annualSavings || annualSavings.toString().trim() === '') {
                          setAnnualSavingsError('Annual Savings is required.');
                          hasError = true;
                        } else {
                          setAnnualSavingsError('');
                        }
                        if (hasError) {
                          setValError('');
                          setShowReviewConfirmModal(false);
                          return;
                        }

                        // Calculate updated fields here
                        const updatedFields = [];
                        const formatVal = (v: any) => (v && v.toString().trim() !== '') ? v.toString() : 'None';
                        const checkField = (name: string, oldV: any, newV: any) => {
                          const o = formatVal(oldV);
                          const n = formatVal(newV);
                          if (o !== n) updatedFields.push(`${name}: '${o}' ➔ '${n}'`);
                        };
                        checkField('Current Cost', idea.validation?.currentCost, currentCost);
                        checkField('Propose Solution', idea.validation?.proposeSolution, proposeSolution);
                        checkField('Saving Type', idea.validation?.savingType, savingType);
                        checkField('Annual Savings', idea.validation?.annualSavings, annualSavings);
                        checkField('One Time Investment', idea.validation?.oneTimeInvestment, oneTimeInvestment);
                        checkField('Owner', idea.validation?.owner, ownerSearchInput);
                        checkField('Timeline', idea.validation?.implementationTimeline, implementationTimeline);
                        checkField('Risk', idea.validation?.risk, risk);
                        checkField('Notes', idea.validation?.notes, notes);

                        const attachmentsChanged = valAttachments.length > 0;
                        if (attachmentsChanged) updatedFields.push('Attachments updated');

                        let updatedFieldsStr = '';
                        if (updatedFields.length > 0) {
                          updatedFieldsStr = `Validation details updated:\n${updatedFields.map(f => `• ${f}`).join('\n')}`;
                        } else if (!idea.validation) {
                          updatedFieldsStr = 'Validation details set';
                        }

                        setShowReviewConfirmModal(false);
                        reviewMutation.mutate({
                          status: 'PENDING_HEAD_APPROVAL',
                          comment: 'Validated by Moderator. Pending Department Head Approval.',
                          validation: {
                            currentCost,
                            proposeSolution,
                            savingType,
                            annualSavings,
                            oneTimeInvestment,
                            owner: ownerSearchInput,
                            implementationTimeline,
                            risk,
                            notes,
                            attachments: valAttachments,
                            updatedFieldsStr
                          }
                        });
                      }}
                      disabled={reviewMutation.isPending}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-colors shadow-md shadow-indigo-600/20 disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {reviewMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <span>Send to Head</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Toast Notification */}
          {showEvalToast && (
            <div className="fixed top-24 right-6 bg-emerald-600 text-white px-5 py-3.5 rounded-xl shadow-lg shadow-emerald-600/30 flex items-center space-x-3 z-50 animate-in slide-in-from-top-5 duration-300">
              <div className="bg-white/20 rounded-full p-1">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-sm tracking-wide">Evaluation submitted successfully!</span>
            </div>
          )}

          {/* Validation Toast Notification */}
          {showValToast && (
            <div className="fixed top-24 right-6 bg-indigo-600 text-white px-5 py-3.5 rounded-xl shadow-lg shadow-indigo-600/30 flex items-center space-x-3 z-50 animate-in slide-in-from-top-5 duration-300">
              <div className="bg-white/20 rounded-full p-1">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-sm tracking-wide">Validation updated successfully!</span>
            </div>
          )}

        </div >
      </div >
    </Layout >
  );
}
